/**
 * OretPage — Workforce Time-Grid (WFM control panel) v2
 *
 * Layout mirrors the reference screenshots:
 *   TOP  | Date nav · Project filter · View toggles · Tolerance · Summary stats
 *   GRID | [Worker name] [15-min time slots ←→] [Weekly totals] [Cumulative totals]
 *
 * Data sources:
 *   - /api/reports/work-schedule-overview?year=&month=   → per-worker day summary
 *   - /api/reports/attendance-daily?worker_id=&year=&month=  → slot-level detail (per worker)
 *   - /api/query/selectworker                            → worker list
 *
 * NOTE: True 10-min slot-level activity typing (Productive/Admin/Break…) requires
 *       per-session break-log data that is not yet in the backend schema.
 *       This page renders the available check_in/check_out window as a solid
 *       "working" block and marks break minutes with a distinct colour.
 *       The right-side totals columns are wired to real data.
 */
import { selectWorkers } from '../utils/scheduleSelection';
import usePermissions from '../hooks/usePermissions';
import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import {
  Box, Typography, IconButton,
  CircularProgress, Stack, TextField, Select,
  MenuItem, FormControl, Paper, Button,
} from '@mui/material';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon       from '@mui/icons-material/Search';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import LocalCafeRoundedIcon  from '@mui/icons-material/LocalCafeRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { exportOretExcel } from '../utils/oretExport';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { leaveType }    from '../config/leaveTypes';
import ScheduleAllocationDialog, { spanMin as allocSpan, toPayload as allocToPayload }
  from '../components/work-schedules/ScheduleAllocationDialog';

/* ─── constants ────────────────────────────────────────────────────────────── */

/**
 * Paleta e rrjetës — një shkallë, jo dyzet nuanca të afërta.
 *
 * Kjo faqe mbante 244 ngjyra të shkruara me dorë në 106 nuanca. Pjesa më e
 * madhe e tyre ishin e njëjta gjë e shkruar pak ndryshe: `#dde3ec`, `#e2e6ee`,
 * `#e0e3ea` dhe `#e8eaf0` janë të katërta «një vijë e zbehtë», por të katërta
 * pak të ndryshme. Asnjëra veç nuk duket gabim; të gjitha bashkë e bëjnë ekranin
 * të duket i palarë, dhe askush s'e gjen dot pse.
 *
 * Shkalla ka hapa të qëllimshëm: sa më e fortë vija, aq më e rëndësishme ndarja
 * që bën — dhjetëshet, gjysmë-ora, ora, skaji i kolonave ngjitëse.
 *
 * Ngjyrat KUPTIMORE (puna, pauza, mungesa, kategoritë) nuk hyjnë këtu: ato
 * mbajnë domethënie dhe jetojnë te `SLOT_COLORS` / `CAT_COLORS`.
 */
const G = {
  paper:      '#ffffff',
  zebra:      '#f8f9fc',   // rreshta ndihmës dhe kolona totalesh
  head:       '#f5f7fb',   // koka e rrjetës
  headAlt:    '#e8edf5',   // koka e kolonave ngjitëse
  hourBg:     '#dde3ec',   // kolona e orës së plotë
  hover:      '#eef1f8',

  // Vijat, nga më e zbehta te më e forta.
  line:       '#f0f2f5',   // ndarëse rreshtash
  lineSoft:   '#e2e6ee',   // ndarëse kolonash 10-minutëshe
  lineMid:    '#c3ced6',   // gjysmë-ora
  lineHard:   '#455a64',   // ora e plotë
  edge:       '#dde3ec',   // skaji i një kolone ngjitëse
  edgeStrong: '#c5cae9',   // skaji i bllokut ngjitës

  // Teksti.
  ink:        '#1a2b47',
  inkStrong:  '#37474f',
  inkMid:     '#546e7a',
  inkSoft:    '#78909c',
  inkFaint:   '#90a4ae',
};

const API = process.env.REACT_APP_API_URL || '';

const MONTHS_SQ = [
  'Janar','Shkurt','Mars','Prill','Maj','Qershor',
  'Korrik','Gusht','Shtator','Tetor','Nëntor','Dhjetor',
];
const WEEKDAYS_SQ_SHORT = ['Die','Hën','Mar','Mër','Enj','Pre','Sht'];

// Summary category colours (top bar)
const CAT_COLORS = {
  productive:         { bg:'#e0f2fe', fg:'#0277bd' },
  productive_paid:    { bg:'#e8f5e9', fg:'#2e7d32' },
  productive_unpaid:  { bg:'#fff9c4', fg:'#f57f17' },
  pause:              { bg:'#fce4ec', fg:'#c62828' },
  admin:              { bg:'#f3e5f5', fg:'#6a1b9a' },
  overtime:           { bg:'#fff3e0', fg:'#e65100' },
  others:             { bg:'#f1f8e9', fg:'#558b2f' },
  absent:             { bg:'#ffebee', fg:'#b71c1c' },
};

// Time slot colours (cell background per activity)
const SLOT_COLORS = {
  work:        '#b9f6ca',   // productive work
  break:       '#ffcdd2',   // pause / break
  overtime:    '#fff3e0',   // overtime
  admin:       '#e1bee7',   // admin
  off:         '#f5f5f5',   // day-off / holiday
  absent:      '#ffebee',   // absent day
  leave:       '#e2e8f0',   // pushim i aprovuar — nuanca e saktë vjen nga lloji
  empty:       '#fafafa',   // no data
};

// Scheduled-hours band — a bold black lane with white text + white outline,
// drawn across each worker's planned working window so it stands out clearly.
const PLAN_BAND_BG     = '#1f1f1f';   // solid black fill for the planned window
const PLAN_BAND_BORDER = '#ffffff';   // white outline around the band
const PLAN_BAND_TEXT   = '#ffffff';   // white activity letters inside the band

// Time grid constants
const SLOT_START = 0;         // 00:00
const SLOT_END   = 24 * 60;   // 24:00 (1440 min)
const CELL_W     = 11;        // px per slot column

/**
 * Sa minuta zë një kolonë — zgjedhje e përdoruesit, jo konstante.
 *
 * Kolona ishte përherë 10-minutëshe. Për një kontroll të shpejtë të ditës kjo
 * është më e imët se ç'duhet (72 kolona për një turn tetëorësh), kurse për të
 * lexuar një vonesë prej pak minutash 30-minutat e fshehin fare atë që kërkohet.
 * Njësia e leximit varet nga puna që bëhet, ndaj e vendos ai që e shikon rrjetën.
 *
 * Vlera shkruhet me dorë, por ulet te pjesëtuesi më i afërt i orës. Kjo nuk
 * është estetikë: me kolonë 7-minutëshe ora e plotë do të binte vetëm çdo shtatë
 * orë, sepse `s * 7` është shumëfish i 60-s vetëm një herë në gjashtëdhjetë
 * kolona — dhe vijat e orës janë i vetmi orientim në një rrjetë prej dhjetëra
 * kolonash. Po ashtu 1440/7 s'është numër i plotë kolonash, dhe ai thyesor do të
 * kërkonte një `new Array()` me gjatësi jo të plotë.
 *
 * Kufiri i poshtëm është 5: nën të, një ditë e plotë kalon një mijë kolona për
 * rresht dhe rrjeta ndalon së lëvizuri pa i treguar askujt asgjë më shumë.
 */
const SLOT_MIN_OPTIONS = [5, 6, 10, 12, 15, 20, 30, 60];
const SLOT_MIN_DEFAULT = 10;
const SLOT_MIN_KEY     = 'oret.slotMin';
const slotCountFor = (slotMin) => (SLOT_END - SLOT_START) / slotMin;

/**
 * Numri i shkruar → vlera e lejuar më e afërt.
 *
 * Fusha pranon çdo gjë gjatë shkrimit, sepse ndryshe «1» do të hidhej sapo të
 * shtypej shkronja e parë e «15». Ulja bëhet vetëm kur vlera konfirmohet, dhe
 * fusha e shfaq atë që u ul — përndryshe ekrani do të tregonte një numër dhe
 * rrjeta do të vizatonte një tjetër.
 *
 * Barazimet shkojnë te vlera më e imët (25 → 20, jo 30): humbja e hollësisë
 * është ajo që s'kthehet dot me zoom.
 */
function snapSlotMin(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return SLOT_MIN_DEFAULT;
  return SLOT_MIN_OPTIONS.reduce(
    (best, opt) => (Math.abs(opt - n) < Math.abs(best - n) ? opt : best),
    SLOT_MIN_OPTIONS[0],
  );
}

/**
 * Preferenca e ruajtur në shfletues.
 *
 * Ruhet lokalisht sepse është mënyrë shikimi, jo e dhënë e kompanisë: dy veta
 * mbi të njëjtin llogari mund ta duan ndryshe. Çdo vlerë jashtë listës — e
 * shkruar me dorë, e mbetur nga një version i vjetër — kthehet te parazgjedhja,
 * përndryshe një `slotMin` i pavlefshëm do të prodhonte kolona gjysmake.
 */
function readStoredSlotMin() {
  try {
    const raw = parseInt(window.localStorage.getItem(SLOT_MIN_KEY), 10);
    if (SLOT_MIN_OPTIONS.includes(raw)) return raw;
  } catch { /* storage i bllokuar — parazgjedhja mban */ }
  return SLOT_MIN_DEFAULT;
}
/**
 * Lartësia e rreshtit — 26px, jo 30.
 *
 * Emri zë rreshtin e parë (13px) dhe kodi me çipat të dytin (11px): njëzet e
 * gjashtë mjaftojnë, dhe katër piksela për njëzet e shtatë punëtorë janë njëqind
 * e tetë — një e treta e një ekrani laptopi, e fituar pa hequr asnjë fjalë.
 */
const ROW_H      = 26;        // px row height
const NAME_W     = 190;       // px worker name column

// Slot activity codes (shown in cells, matching FlixBus grid)
const SLOT_LABELS = {
  work:     '4',    // productive
  break:    'p',    // pause
  overtime: 'u4',   // overtime
  admin:    'a4',   // admin
  off:      '',     // day-off
  absent:   'a',    // absent
  leave:    'P',    // pushim i aprovuar
  empty:    '',     // no data
};

/* ─── Monitorimi: plani kundrejt prezencës reale ───────────────────────────
 *
 * Ngjyrat vijnë të gatshme nga serveri si segmente
 * (`backend/services/scheduleMonitoring.js`): ky skedar nuk vendos se ku fillon
 * një mungesë — vetëm e vizaton atë. Pa këtë ndarje, i njëjti turn do të
 * gjykohej ndryshe nga rrjeta dhe nga një raport.
 *
 * `mark` nuk është zbukurim: ngjyra nuk guxon të jetë i vetmi informacion. Çdo
 * qelizë mban një shenjë dhe një `title` me intervalin e saktë, ndaj gjendja
 * lexohet edhe pa dalluar të verdhën nga e kuqja. */
/**
 * Statuset e monitorimit — paletë e markës, jo e katalogut të Material-it.
 *
 * Ishte `#FDD835` / `#E53935` / `#FFB74D`: tri ngjyra të pastra nga paleta
 * standarde, dhe tri probleme bashkë me to.
 *
 *  1. **E verdha ishte gjendja NORMALE.** Në çdo panel, e verdha do të thotë
 *     «kujdes» — kurse këtu mbulonte pikërisht rastin kur gjithçka është në
 *     rregull: njeriu është në punë. Sa më e mirë dita, aq më shumë alarm dukej.
 *  2. **E verdha dhe portokallia janë fqinje.** «Në punë» dhe «tolerancë»
 *     duheshin dalluar në një kolonë 11px të gjerë.
 *  3. **Asnjëra s'i takonte kësaj marke.** Petroli i logos rri dy centimetra më
 *     lart te i njëjti ekran.
 *
 * Tani gjendja normale është vetë petroli i markës (`theme.palette.primary.dark`),
 * i qetë; përjashtimet janë të ngrohta dhe rriten sipas peshës: qelibar për një
 * vonesë të falur, e kuqe për mungesë. Shiriti i planit, që pa monitorim
 * vizatohet i zi, kështu bëhet shirit statusi në ngjyrat e vetë kompanisë.
 *
 * **Ngjyra nuk e mban kurrë gjendjen vetëm.** Dy çifte kanë gati të njëjtën
 * ndriçim (petrol/e kuqe, qelibar/gri-blu): në bardhezi ose me daltonizëm ato
 * nuk dallohen dot. Prandaj secila mban shenjën e vet (`✓ ! ~ ·`), dhe atë e
 * mbron një test më vete.
 */
const MONITOR_COLORS = {
  // Petroli i markës si TINT (42% mbi të bardhë), jo i plotë.
  //
  // I ngopur, «në punë» mbulonte gjysmën e ekranit me të njëjtën peshë si
  // mungesa — dhe dy ngjyra të forta që luftojnë e barazojnë normën me
  // përjashtimin. Norma duhet të jetë e qetë: ajo s'kërkon vëmendje, ajo
  // është ajo që pritej.
  covered: { bg: '#A3C0C6', fg: '#0F323B', mark: '✓' },   // tint i petrolit — si u planifikua
  missing: { bg: '#CE3F3F', fg: '#FFFFFF', mark: '!' },   // mungesë në orar
  // «Pa ardhur» ishte `#ECEFF1` mbi një rrjetë ku qeliza bosh është `#fafafa`:
  // dy nuanca të bardha, e para e padallueshme nga e dyta. Dhe kjo gjendje mbulon
  // TË GJITHË orarin e mbetur të ditës — pra pikërisht shiriti i planit, ai që pa
  // monitorim vizatohet i zi, zhdukej sapo ndizej monitorimi.
  //
  // Tani është gri-blu e mbushur: dukshëm më e errët se vijat e rrjetës, dhe e
  // ftohtë me qëllim — plani që ende s'ka ardhur nuk duhet të konkurrojë me të
  // verdhën e punës apo të kuqen e mungesës.
  pending: { bg: '#9FB0BD', fg: '#16232C', mark: '·' },   // ende pa ardhur
};
// Vonesa brenda tolerancës mbetet mungesë — thjesht nuk bërtet si mungesë.
// Qelibar i shuar: një shkallë nën të kuqen, dhe dukshëm mbi gri-blunë e planit.
const MONITOR_TOLERATED = { bg: '#D08F2B', fg: '#3A2600', mark: '~' };

/** Minuta nga mesnata → 'HH:MM', për tooltip-at. */
const fmtClock = (m) => {
  const v = ((Math.round(m) % 1440) + 1440) % 1440;
  return `${String(Math.floor(v / 60)).padStart(2,'0')}:${String(v % 60).padStart(2,'0')}`;
};

/**
 * Ngjyra e një segmenti monitorimi — ose asnjë.
 *
 * **E ardhmja s'është verdikt.** Monitorimi i përgjigjet pyetjes «a ndodhi», dhe
 * për orët që s'kanë ardhur ende ai s'ka çfarë të përgjigjet: një ngjyrë e tij
 * aty do të ishte verdikt i shpikur. Prandaj `pending` kthen `null` dhe qeliza
 * bie te shtresa e planit — ngjyra e llojit të punës, ose shiriti i zi.
 *
 * Kjo u ndreq në dy hapa, dhe hapi i parë ishte gjysmak: fillimisht heshtja u
 * kufizua te ata që kishin ardhur. Po «pa ardhur» mbetej gri për të gjithë të
 * tjerët — pra dy të tretat e ekranit gri, dhe plani i tyre i padukshëm. Fakti
 * «s'ka ardhur» thuhet tashmë nga E KUQJA mes fillimit të turnit dhe tanishmes;
 * shtrirja e tij mbi tërë të ardhmen s'shtonte informacion, vetëm zhurmë.
 *
 * `arrived` mbetet te segmenti dhe te tooltip-i: aty ndryshon fjala, jo ngjyra.
 */
const monitorPaint = (seg) => {
  if (seg?.tolerated) return MONITOR_TOLERATED;
  // `pending` NUK pikturon. E ardhmja s'është verdikt — është plan.
  if (seg?.state === 'pending') return null;
  // As `break`: pauza e planifikuar e ka ngjyrën e vet (BREAK_BG), dhe një
  // verdikt monitorimi mbi të do ta mbulonte pikërisht atë që duhet të dallohet.
  if (seg?.state === 'break') return null;
  return MONITOR_COLORS[seg?.state];
};

/**
 * Paleta e shiritit — e nxjerrë nga LOGOJA, jo e zgjedhur veç për këtë ekran.
 *
 * `img/nexuma-logo-official.svg` ka saktësisht dy ngjyra: petrolin `#4893A5`
 * dhe blunë e ngrysur `#303D50`. Të njëjtat rrjedhin edhe te `app/theme.js`
 * ("Nexuma brand teal" `#3E8FA3`). Shiriti nisi me një blu të ftohtë gati të
 * zezë dhe me jeshilen/blunë e Material-it — ngjyra që nuk i takonin askujt.
 *
 * Prandaj:
 *
 *   · SIPËRFAQET janë blu-ja e logos e errësuar në dy shkallë. Një gri neutrale
 *     do të ishte e sigurt dhe e huaj; kjo e mban shiritin brenda familjes.
 *   · THEKSI është petroli i markës — një i vetëm. Dy hue-e që garojnë (jeshile
 *     për pauzat, blu për projektet) e bënin shiritin të dukej si dy vegla të
 *     ngjitura, dhe asnjëra prej të dyjave s'ishte ngjyrë e Nexuma-s.
 *   · JESHILJA, PORTOKALLIA DHE E KUQJA mbeten VETËM për gjendje — sukses,
 *     paralajmërim, gabim — dhe vlerat janë ato të `theme.js`, jo të përafërta.
 *     Ky është pikërisht rregulli që shkruan vetë tema: "semantic colors
 *     reserved for status only".
 *
 * `panel` është një shkallë MË E NDRITSHME se `surface`: seksionet e hapura
 * duhet të lexohen si shtresë mbi shirit, jo si vrimë në të.
 */
const QA = {
  surface:   '#1A2331',                  // #303D50 i errësuar — trupi i shiritit
  panel:     '#232F41',                  // një shkallë mbi të — seksionet e hapura
  line:      'rgba(255,255,255,.08)',
  field:     'rgba(255,255,255,.055)',
  fieldEdge: 'rgba(255,255,255,.12)',
  text:      '#E7EDF1',
  dim:       '#8CA3B4',                  // etiketa dhe tekst dytësor
  faint:     '#5C7387',                  // shenja, njësi, placeholder

  // Theksi i markës — petroli i logos. Një i vetëm për gjithë shiritin.
  brand:     '#4893A5',
  brandSoft: '#7FC3D4',
  brandDark: '#256878',
  brandBg:   'rgba(72,147,165,.16)',
  brandEdge: 'rgba(72,147,165,.42)',

  // Gjendjet — vlerat e `app/theme.js`, jo të përafërta.
  success:   '#10B981', successSoft: '#34D399',
  warning:   '#F59E0B', warningSoft: '#FBBF24',
  error:     '#EF4444',
};

/**
 * Kolonat e së djathtës — vetëm ato që kanë të dhëna.
 *
 * Këtu ishte edhe «Admin D31». Ajo lexohej nga `adminMin`, e cila ishte shkruar
 * `0` me një shënim «not yet in data model». Pra kolona nuk ishte bosh sepse
 * askush s'kishte bërë punë administrative — ishte bosh sepse sistemi nuk e mat
 * fare atë. Një zero e tillë nuk është informacion; është një pohim i rremë që
 * kushdo do ta lexonte si «zero orë admin».
 */
const RIGHT_COL_DEFS = [
  { key: 'day_productive', labelFn: (d) => `Prod. D${d}`, titleKey:'oret.hintProdDay' },
  { key: 'day_total',      labelFn: (d) => `Tot. D${d}`,  titleKey:'oret.hintTotDay' },
  { key: 'till_day',       labelFn: (d) => `Deri D${d}`,  titleKey:'oret.hintUntilDay' },
];
/*
 * Blloku «Hour Distribution» (Productive · Admin · Others · Training · Absence ·
 * Total Regular) u hoq.
 *
 * Katër nga gjashtë kolonat e tij vinin nga fusha që serveri i mbush me zero të
 * ngurtë (`admin: 0, others: 0, training: 0`), «Productive» ishte i njëjti numër
 * me «Total*», dhe «Total (Regular)» në fakt mbante orët e PLANIFIKUARA — pra
 * një etiketë që thoshte diçka tjetër nga përmbajtja. Gjashtë kolona për një
 * shifër të vërtetë: mungesat. Ajo shifër u zhvendos te totalet e mëdha.
 */
/**
 * Kolonat e totaleve — 54px, jo 62.
 *
 * Përmbajtja e tyre është gjithmonë `H:MM` (pesë shenja te 10.5px ≈ 34px) plus
 * hapësira. Tetë piksela × dymbëdhjetë kolona janë njëqind — pikërisht sa një
 * orë e gjysmë rrjete, që lexohet, kundrejt hapësirës boshe që s'lexohet.
 */
const RIGHT_COL_W = 54;
const TL_COL_W   = 110; // teamleader column width
const PROJ_COL_W = 100; // project column width
const DEPT_COL_W = 120; // department column width

const fmtH = (min) => {
  if (min == null || min === '') return '—';
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.abs(min) % 60;
  return `${min < 0 ? '-' : ''}${h}:${String(m).padStart(2,'0')}`;
};

function getAuthHeaders() {
  // The app stores the session (with token) under `logedInUser` in sessionStorage,
  // with a cookie fallback for "remember me". Read from there — `hr_token` is never set.
  let token = '';
  try {
    const raw = sessionStorage.getItem('logedInUser');
    if (raw) token = JSON.parse(raw)?.token || '';
    if (!token) {
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
      if (cookie) {
        const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
        token = JSON.parse(val)?.token || '';
      }
    }
  } catch { /* noop */ }
  return token ? { Authorization: `Bearer ${token}` } : {};
}
const localStr = (d) => {
  const n = new Date(d);
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
};
const TODAY = localStr(new Date());
/**
 * 'YYYY-MM-DD' → Date në orën LOKALE.
 *
 * `new Date('2026-09-19')` e lexon si mesnatë UTC: në një zonë pas UTC-së
 * (p.sh. agjentë në Amerikë) data lokale bie një ditë prapa, dhe shigjetat
 * «dita para/pas» e ekrani i ditës tregojnë një ditë tjetër nga ajo e zgjedhur.
 */
const parseLocalDate = (ds) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ds || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(ds);
};

/**
 * Periudha që shikohet. Filtri ishte vetëm një datë, dhe rrjeta e orës ka kuptim
 * vetëm për një ditë — kolonat e saj janë minuta. Për intervale më të gjata
 * kolonat ndërrojnë kuptim: bëhen DITË, dhe qeliza mban orët e asaj dite.
 *
 * Të dhënat nuk kërkuan asgjë të re nga serveri: `work-schedule-overview` e
 * ngarkonte tërë muajin që në fillim (`w.days[date]` për çdo ditë) — vetëm rrjeta
 * e minutave ishte e kufizuar te një datë. Javë dhe muaj ishin aty, të palexuara.
 */
const PERIODS = [
  { key: 'day',     labelKey: 'oret.periodDay'     },
  { key: 'week',    labelKey: 'oret.periodWeek'    },
  { key: 'month',   labelKey: 'oret.periodMonth'   },
  { key: 'quarter', labelKey: 'oret.periodQuarter' },
];

/** Fillimi i javës është e hënë — java e punës, jo ajo e kalendarit amerikan. */
function weekStart(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;          // Hën=0 … Die=6
  x.setDate(x.getDate() - dow);
  return x;
}

/**
 * Intervali i periudhës. «3 muaj» është dritarja që MBARON me muajin e zgjedhur,
 * jo tremujori i kalendarit: kush shkruan 10 shtator do të shohë korrik–shtator,
 * jo korrik–shtator vetëm sepse ashtu bie tremujori.
 */
function periodRange(dateStr, period) {
  const d = parseLocalDate(dateStr);
  if (period === 'week') {
    const a = weekStart(d); const b = new Date(a); b.setDate(a.getDate() + 6);
    return { from: localStr(a), to: localStr(b) };
  }
  if (period === 'month') {
    return { from: localStr(new Date(d.getFullYear(), d.getMonth(), 1)),
             to:   localStr(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
  }
  if (period === 'quarter') {
    return { from: localStr(new Date(d.getFullYear(), d.getMonth() - 2, 1)),
             to:   localStr(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
  }
  return { from: dateStr, to: dateStr };
}

/** Numri i javës ISO — për kokat e pamjes tremujore. */
function isoWeek(d) {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  x.setUTCDate(x.getUTCDate() + 4 - (x.getUTCDay() || 7));
  return Math.ceil(((x - Date.UTC(x.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
}

/**
 * Njësia e kolonës ndjek GJATËSINË e periudhës, jo dëshirën.
 *
 * Javë dhe muaj lexohen ditë për ditë — shtatë ose tridhjetë kolona. Tre muaj jo:
 * nëntëdhjetë kolona janë katër mijë piksela, dhe askush s'kërkon «a erdhi me
 * vonesë më 12 korrik» duke shikuar një tremujor. Aty pyetja është si shkuan
 * JAVËT, ndaj kolona bëhet javë.
 *
 * I njëjti parim që e ndau rrjetën e minutave nga ajo e ditëve, i çuar një hap
 * më tej: shkalla e kolonës i përgjigjet pyetjes që bën ajo shkallë.
 */
function periodColumns(dates, period) {
  if (period !== 'quarter') {
    return dates.map((d) => ({ key: d, kind: 'day', dates: [d] }));
  }
  const out = [];
  for (const d of dates) {
    const ws = localStr(weekStart(new Date(d)));
    const last = out[out.length - 1];
    if (last && last.key === ws) last.dates.push(d);
    else out.push({ key: ws, kind: 'week', dates: [d] });
  }
  return out;
}

/**
 * Statusi i një kolone me disa ditë: më i rëndi fiton.
 *
 * Një javë me katër ditë të mbara dhe një mungesë nuk është javë e mbarë. Mesatarja
 * do ta fshihte pikërisht atë ditë që dikush duhet ta shohë.
 */
const STATUS_WEIGHT = { absent: 4, late: 3, leave: 2, excused: 2, on_time: 1, scheduled: 0, day_off: 0 };
function worstStatus(days) {
  let best = null, w = -1;
  for (const d of days) {
    const cur = STATUS_WEIGHT[d?.status] ?? 0;
    if (d?.status && cur > w) { w = cur; best = d.status; }
  }
  return best || 'scheduled';
}

/** Çdo datë e intervalit, si vargje `YYYY-MM-DD`. */
function datesBetween(from, to) {
  const out = []; const d = new Date(from); const end = new Date(to);
  while (d <= end) { out.push(localStr(d)); d.setDate(d.getDate() + 1); }
  return out;
}

/**
 * Ngjyra e një dite te matrica — nga STATUSI, jo nga orët.
 *
 * Orët i thotë numri brenda qelizës; ngjyra i përgjigjet pyetjes tjetër: a shkoi
 * dita si duhej. Prandaj «vonesë» dhe «në kohë» ndahen edhe kur orët janë njësoj.
 */
const DAY_STATUS = {
  on_time:   { bg:'#E3F0EC', fg:'#1F5F52', key:'oret.dayOnTime' },
  late:      { bg:'#FBEFD8', fg:'#7A5310', key:'oret.dayLate' },
  absent:    { bg:'#F8E0E0', fg:'#8E2B25', key:'oret.missing' },
  leave:     { bg:'#E7EDF5', fg:'#334E68', key:'oret.leave' },
  // Mungesë e arsyetuar nga fjalori (sëmundje, leje). E ftohtë me qëllim: nuk
  // është problem që kërkon vëmendje, është një ditë e llogaritur.
  excused:   { bg:'#E4EAF2', fg:'#2F4A63', key:'oret.dayExcused' },
  day_off:   { bg:'#F4F5F7', fg:'#9AA5B1', key:'oret.dayOff' },
  scheduled: { bg:'#FFFFFF', fg:'#9AA5B1', key:'oret.dayScheduled' },
};
const dayStatusOf = (st) => DAY_STATUS[st] || DAY_STATUS.scheduled;

/**
 * Segmentet e monitorimit → një qelizë për çdo kolonë të rrjetës.
 *
 * Një turn nate del mbi 1440 minuta; ai bisht mbështillet në fillim TË TË NJËJTIT
 * rresht dhe shënohet `nextDay`, që rrjeta një-ditore ta tregojë të plotë.
 */
function buildMonitorSlots(monitor, slotMin) {
  if (!Array.isArray(monitor) || !monitor.length) return null;
  const count = slotCountFor(slotMin);
  const cells = new Array(count).fill(null);
  for (const seg of monitor) {
    const from = Math.floor(seg.from / slotMin);
    const to   = Math.ceil(seg.to / slotMin);
    for (let i = from; i < to; i++) {
      const wrapped = i >= count;
      const idx = wrapped ? i - count : i;
      if (idx < 0 || idx >= count) continue;
      // Pjesa e ditës aktuale fiton mbi bishtin e mbështjellë.
      if (wrapped && cells[idx]) continue;
      cells[idx] = { ...seg, nextDay: wrapped };
    }
  }
  return cells;
}

/* ─── parse a time string like "08:30" or ISO timestamp → minutes since midnight ── */
const toMin = (ts) => {
  if (!ts) return null;
  try {
    const d = new Date(ts);
    if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
    const parts = String(ts).match(/(\d{1,2}):(\d{2})/);
    if (parts) return parseInt(parts[1],10)*60 + parseInt(parts[2],10);
  } catch {}
  return null;
};

/**
 * Ngjyra e një dite me leje — nga `config/leaveTypes.js`, katalogu i vetëm.
 *
 * Aty çdo lloj e ka tashmë ngjyrën e vet (vjetori blu, mjekësori i kuq, lehonia
 * rozë…), të njëjtat që përdoren te kalendari dhe te kërkesat. Një listë e dytë
 * ngjyrash këtu do të thoshte që i njëjti pushim të kishte dy ngjyra në dy
 * ekrane — dhe njëra prej tyre do të vjetrohej pa e vënë re askush.
 */
function leaveTint(typeId) {
  const t = leaveType(typeId);
  return t?.color || '#64748B';
}

/** Sfondi i qelizës: i zbehtë, që kodi i aktivitetit të mbetet i lexueshëm. */
const leaveBg = (typeId) => `${leaveTint(typeId)}2e`;

/**
 * Ngjyra e një tipi të arsyetuar mbi shiritin e planit — TINT, jo e plotë.
 *
 * Ngjyra e `SICK_LEAVE` te fjalori është `#DC2626`, gati identike me të kuqen e
 * «Mungesë» (`#CE3F3F`). E vizatuar solide, një ditë sëmundjeje do të bërtiste
 * saktësisht si mungesa që ky ndryshim erdhi ta ndalte — dhe ngjyra e ka fjalën
 * e parë, para se dikush të lexojë shenjën.
 *
 * I njëjti zgjidhje që lejet e aprovuara e kanë prej kohësh (`leaveBg`): tint i
 * lehtë me shkrim në ngjyrën e plotë. Identiteti mbetet, alarmi bie.
 */
const excusedBg   = (hex) => `${hex || '#64748B'}2e`;
const excusedInk  = (hex) => hex || '#334E68';

/**
 * Pauza vizatohet me IKONË, jo me emoji.
 *
 * Emoji-t vijnë me ngjyrat e veta — një filxhan kafeje ngjyrë kafeje mbi një
 * bllok ambër — dhe secili shfletues e vizaton ndryshe. Ikona e trashëgon
 * `color`, ndaj pauza duket e njëjtë kudo dhe rri brenda paletës.
 *
 * Ambri është identiteti i pauzës, i njëjti për të dyja: një pauzë është pauzë,
 * dhe cila prej tyre e thotë FORMA — thikë e pirun për drekën, filxhan për atë
 * të shkurtër. Lloji i saktë nga fjalori mbetet te tooltip-i, ku ka vend për emër.
 */
const BREAK_INK = '#B4720E';
const BREAK_BG  = '#FBEAD0';
const BreakIcon = ({ token }) => {
  const Ico = token === '@lunch' ? RestaurantRoundedIcon : LocalCafeRoundedIcon;
  return <Ico sx={{ fontSize: 11, color: BREAK_INK, verticalAlign: 'middle' }} />;
};
const isBreakToken = (v) => v === '@lunch' || v === '@coffee';

/**
 * Çfarë bëri gjatë turnit — llojet që shkruan vetë ora e punës.
 *
 * `attendance_sessions.breaks` i mban këto gjashtë: `lunch`, `short`, `break`,
 * `meeting`, `gap`, `auto_idle`. Deri tani prej tyre lexohej vetëm shuma, ndaj
 * një mbledhje dy-orëshe dhe një drekë gjysmë-orëshe dukeshin e njëjta gjë:
 * «45 minuta pauzë».
 *
 * `mark` nuk është zbukurim — ngjyra nuk guxon të jetë i vetmi informacion mbi
 * një qelizë 11-pikëshe.
 */
const ACTIVITY = {
  lunch:     { bg:BREAK_BG, fg:BREAK_INK, mark:'@lunch',  key:'oret.actLunch' },
  short:     { bg:BREAK_BG, fg:BREAK_INK, mark:'@coffee', key:'oret.actShort' },
  break:     { bg:BREAK_BG, fg:BREAK_INK, mark:'@coffee', key:'oret.actBreak' },
  meeting:   { bg:'#DDD6FE', fg:'#4C1D95', mark:'M', key:'oret.actMeeting' },
  gap:       { bg:'#E2E8F0', fg:'#334155', mark:'—', key:'oret.actGap' },
  auto_idle: { bg:'#E5E7EB', fg:'#374151', mark:'i', key:'oret.actIdle' },
};
const activityOf = (t) => ACTIVITY[t] || ACTIVITY.break;

/**
 * Pauzat shkruhen me SIMBOL, jo me emër.
 *
 * Një pauzë zgjat dhjetë deri dyzet e pesë minuta — një deri katër kolona. Aty
 * s'hyn as «Drekë», as inicialet, ndaj shkalla e tretë e etiketës e linte bosh
 * dhe pauza dukej vetëm si ndërprerje ngjyre. Një simbol hyn kudo, dhe dallon
 * pushimin e gjatë nga ai i shkurtër pa asnjë fjalë:
 *
 *   ☕ pauza e ORARIT (dreka)  ·  ⏸ pauzë e shkurtër shtesë
 *
 * Simboli s'ka gjuhë — dhe kjo është arsyeja e dytë pse është zgjidhja e duhur
 * këtu: rrjeta lexohet njësoj në shqip, anglisht e gjermanisht.
 */
const BREAK_SYMBOL = { long: '@lunch', short: '@coffee' };
const breakSymbol = (sub) => BREAK_SYMBOL[sub] || BREAK_SYMBOL.short;


/**
 * Cili aktivitet zbritet nga çfarë — një vend, jo tri lista të shpërndara.
 *
 * `PAUSE` largohet nga «Punuar»: pushimi nuk është punë. `NOT_PRODUCTIVE` mbetet
 * brenda «Punuar» (njeriu ishte këtu, i paguar) por del nga «Produktive», sepse
 * një orë mbledhjeje nuk është një orë prodhimi.
 */
const PAUSE_ACTIVITIES = new Set(['lunch', 'short', 'break']);
const NOT_PRODUCTIVE   = new Set(['meeting', 'gap', 'auto_idle']);

/**
 * Intervalet e aktivitetit → një qelizë për çdo kolonë të rrjetës.
 *
 * I fundit shkruan mbi të parin me qëllim: kur dy intervale mbivendosen, ai që
 * u regjistrua më vonë është korrigjimi i të parit.
 */
function buildActivitySlots(activities, slotMin) {
  if (!Array.isArray(activities) || !activities.length) return null;
  const count = slotCountFor(slotMin);
  const cells = new Array(count).fill(null);
  for (const a of activities) {
    const from = Math.floor(a.from / slotMin);
    const to   = Math.ceil(a.to / slotMin);
    for (let i = from; i < to; i++) {
      const idx = i >= count ? i - count : i;
      if (idx >= 0 && idx < count) cells[idx] = a;
    }
  }
  return cells;
}

/**
 * Plani i tipizuar mbi boshtin e kolonave.
 *
 * Serveri i dërgon pjesët si intervale minutash (`day.plan`), të renditura me
 * pauzat MBI punën; kjo vetëm i vendos mbi kolona, si `buildActivitySlots`.
 *
 * **Vetëm ajo që u ZGJODH ngjyroset.** Një segment pa kod trashëgon shabllonin,
 * pra s'është vendim i askujt — dhe po ta ngjyrosnim, çdo orar do të bëhej blu
 * pa e prekur njeri, kurse e zeza e shiritit të planit ekziston pikërisht që
 * orari të jetë i pangatërrueshëm. Pauza është përjashtim: ajo është vetë një
 * vendim, edhe pa lloj të zgjedhur.
 */
function buildPlanSlots(plan, slotMin) {
  if (!Array.isArray(plan) || !plan.length) return null;
  const count = slotCountFor(slotMin);
  const cells = new Array(count).fill(null);
  let painted = false;
  for (const part of plan) {
    if (part.kind === 'work' && !part.code) continue;
    painted = true;
    const from = Math.floor(part.from / slotMin);
    const to   = Math.ceil(part.to / slotMin);
    for (let i = from; i < to; i++) {
      const idx = i >= count ? i - count : i;
      if (idx >= 0 && idx < count) cells[idx] = part;
    }
  }
  return painted ? cells : null;
}

/**
 * Totalet e një punëtori për një interval — NJË llogaritje, dy lexues.
 *
 * Shiriti dhe eksporti duhet të thonë të njëjtën gjë; po t'i shkruaje dy herë,
 * do të pajtoheshin derisa njëra të ndryshonte, dhe atëherë skedari që shkon te
 * klienti do të mospajtohej me ekranin ku u pa. Prandaj rri këtu, e pastër, dhe
 * provohet pa hapur asnjë ekran.
 */
/**
 * Minutat e pranisë nga hyrjet/daljet e ditës — bashkim, pa mbivendosje.
 *
 * Deri tani prania merrej nga `monitor_totals.covered`, i cili është i PRERË te
 * dritarja e planit dhe i heq minutat e pauzës së planifikuar. Pasoja ishin dy:
 *   · kush punonte jashtë orarit (07:30–18:00 me plan 08:00–16:00) numërohej
 *     vetëm 8 orë te «Punuar», ndërsa «Overtime» pranë tij tregonte 2 orë;
 *   · pauza zbritej dy herë — një herë nga `covered`, një herë këtu.
 * Intervalet janë ajo çka ndodhi vërtet; plani gjykohet veç, te monitorimi.
 *
 * @param {Array} intervals  [{ start, end }] minuta nga mesnata; `end` null = i hapur
 * @param {number|null} nowCap  minutat e tanishme (vetëm për sot); null = dita ka mbaruar
 */
export function presenceMinutes(intervals, nowCap = null) {
  const list = (intervals || []).map((iv) => {
    const st = Number(iv?.start);
    if (!Number.isFinite(st)) return null;
    let en = iv?.end == null ? NaN : Number(iv.end);
    // Sesion i hapur: sot shtrihet deri tani, një ditë e mbyllur nuk mbulon asgjë.
    if (!Number.isFinite(en)) en = nowCap == null ? st : Math.max(st, nowCap);
    if (en < st) en += 1440;                       // dalja pas mesnate
    return { st, en };
  }).filter(Boolean).sort((a, b) => a.st - b.st);

  let total = 0, curS = null, curE = null;
  for (const iv of list) {
    if (curE === null) { curS = iv.st; curE = iv.en; continue; }
    if (iv.st > curE) { total += curE - curS; curS = iv.st; curE = iv.en; }
    else curE = Math.max(curE, iv.en);
  }
  if (curE !== null) total += curE - curS;
  return Math.max(0, total);
}

export function sumWorkerDays(days = {}, dates = []) {
  const nowCapToday = (() => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); })();
  const out = { worked: 0, productive: 0, paused: 0, planned: 0, plannedBreak: 0,
                overtime: 0, late: 0, absentDays: 0, workDays: 0, live: false,
                // Plani i ndarë: sa orë për çdo lloj pune, dhe kur bien pauzat.
                // `Map` sepse renditja e llojeve duhet të mbetet ajo e planit;
                // `Set` sepse i njëjti orar pauze përsëritet çdo ditë të javës.
                byType: new Map(), breakTimes: new Set(),
                // Pushimet sipas llojit (TypeId → { label, days }); gjysmë dite = 0.5.
                leaves: new Map(), leaveDays: 0,
                // Orët e punuara në ditë pushimi (fundjavë/festë): brenda
                // «Punuar», por të matshme veç.
                dayOff: 0, dayOffDays: 0 };
  for (const key of dates) {
    const d = days?.[key];
    if (!d) continue;

    /**
     * Puna në një ditë pushimi numërohet.
     *
     * Deri tani çdo ditë me `is_work_day=false` kapërcehej fare: agjenti që
     * punonte të shtunën i kishte ato orë te rrjeta, por asnjë pilulë dhe asnjë
     * rresht Excel-i nuk i tregonte. Këtu hyjnë te «Punuar» (dhe veç, te
     * «Fundjavë»), por JO te dita e punës, plani apo mungesat — atë ditë
     * s'kishte plan për t'u mbushur.
     */
    if (!d.is_work_day) {
      const ivsOff = Array.isArray(d.intervals) ? d.intervals : null;
      const isTodayOff = key === TODAY;
      if (isTodayOff && ivsOff?.some((iv) => iv?.end == null)) out.live = true;
      const presOff = ivsOff && ivsOff.length
        ? presenceMinutes(ivsOff, isTodayOff ? nowCapToday : null)
        : (d.net_worked_minutes || 0) + (d.break_minutes || 0);
      let pauseOff = 0, unprodOff = 0;
      for (const a of d.activities || []) {
        const m = Math.max(0, (a.to ?? 0) - (a.from ?? 0));
        if (PAUSE_ACTIVITIES.has(a.type)) pauseOff += m;
        else if (NOT_PRODUCTIVE.has(a.type)) unprodOff += m;
      }
      const pOff = (d.activities || []).length ? pauseOff : (d.break_minutes || 0);
      const netOff = Math.max(0, presOff - pOff);
      if (netOff > 0) {
        out.worked += netOff;
        out.productive += Math.max(0, netOff - unprodOff);
        out.paused += pOff;
        out.dayOff += netOff;
        out.dayOffDays++;
      }
      continue;
    }

    out.workDays++;
    if (d.leave) {
      const n = d.leave.HalfDay ? 0.5 : 1;
      const cur = out.leaves.get(d.leave.TypeId) || { typeId: d.leave.TypeId, label: d.leave.Label, days: 0 };
      cur.days += n; out.leaves.set(d.leave.TypeId, cur);
      out.leaveDays += n;
    }

    // Minutat sipas llojit të aktivitetit, nga vetë ora e punës.
    let pauseMin = 0, unproductiveMin = 0;
    for (const a of d.activities || []) {
      const m = Math.max(0, (a.to ?? 0) - (a.from ?? 0));
      if (PAUSE_ACTIVITIES.has(a.type)) pauseMin += m;
      else if (NOT_PRODUCTIVE.has(a.type)) unproductiveMin += m;
    }

    // Prezenca: nga hyrjet/daljet e ditës; pa to, totali i sesioneve të mbyllura.
    const ivs = Array.isArray(d.intervals) ? d.intervals : null;
    const isToday = key === TODAY;
    const open = !!ivs?.some((iv) => iv?.end == null);
    if (isToday && open) out.live = true;          // dita ende në ecuri
    const presence = ivs && ivs.length
      ? presenceMinutes(ivs, isToday ? nowCapToday : null)
      : (d.net_worked_minutes || 0) + (d.break_minutes || 0);

    // Kur s'ka aktivitete të regjistruara, pauza e planifikuar mbetet e vetmja
    // e njohur — më mirë ajo se zero, dhe kurrë të dyja bashkë.
    const pause = (d.activities || []).length ? pauseMin : (d.break_minutes || 0);

    out.planned      += d.planned_minutes || 0;
    out.plannedBreak += d.planned_break_minutes || 0;

    const net = Math.max(0, presence - pause);
    out.worked     += net;
    out.productive += Math.max(0, net - unproductiveMin);
    out.paused     += pause;
    if (d.status === 'absent') out.absentDays++;
    if (d.late_minutes > 0) out.late += d.late_minutes;
    if (d.overtime_minutes > 0) out.overtime += d.overtime_minutes;

    for (const t of d.plan_summary?.byType || []) {
      const k = t.code || t.name || '_';
      const cur = out.byType.get(k) || { code: t.code || null, name: t.name, minutes: 0 };
      cur.minutes += t.minutes || 0;
      out.byType.set(k, cur);
    }
    for (const b of d.plan_summary?.breaks || []) {
      out.breakTimes.add(`${b.sub === 'long' ? '☕' : '·'} ${b.from}–${b.to}`);
    }
  }
  return out;
}

/* ─── build slot array for one worker-day ────────────────────────────────── */
/**
 * Kodet e aktivitetit për një ditë pune.
 *
 * Këtu kishte edhe një parametër `tolerance` që nuk lexohej askund në trup:
 * toleranca zbatohet te `services/scheduleMonitoring.js`, ku bëhet ndarja e
 * vërtetë. Një parametër që nuk ndryshon asgjë premton një sjellje që s'ka.
 */
function buildSlots(dayData, slotMin) {
  const count = slotCountFor(slotMin);
  const slots = new Array(count).fill('empty');
  if (!dayData) return slots;
  if (!dayData.is_work_day) { slots.fill('off'); return slots; }
  // Leja e aprovuar rri para mungesës: dita ka arsye, dhe arsyeja ka ngjyrë.
  if (dayData.leave && !dayData.check_in) { slots.fill('leave'); return slots; }
  if (dayData.status === 'absent' || dayData.compliance_status === 'absent') {
    slots.fill('absent'); return slots;
  }

  const ci  = toMin(dayData.check_in);
  const co  = toMin(dayData.check_out);
  if (ci == null) return slots;

  const coMin = co ?? (ci + (dayData.net_worked_minutes || 480));

  // Fill work slots
  const startSlot = Math.floor(ci    / slotMin);
  const endSlot   = Math.ceil(coMin  / slotMin);
  for (let s = startSlot; s < endSlot && s < count; s++) {
    slots[s] = 'work';
  }

  // Mark break in the middle (estimated)
  const brMin = dayData.break_minutes || 0;
  if (brMin > 0) {
    const midSlot  = Math.floor((startSlot + endSlot) / 2);
    const brSlots  = Math.max(1, Math.floor(brMin / slotMin));
    for (let s = midSlot; s < midSlot + brSlots && s < count; s++) {
      slots[s] = 'break';
    }
  }

  // Overtime
  const sched = toMin(dayData.schedule_end);
  if (sched != null && coMin > sched) {
    const otStart = Math.floor(sched / slotMin);
    for (let s = otStart; s < endSlot && s < count; s++) {
      if (slots[s] === 'work') slots[s] = 'overtime';
    }
  }

  return slots;
}

/* ─── Summary stat pill ─────────────────────────────────────────────────────── */
/** Çfarë mat secila pilulë — që numri të mos duhet marrë me mend. */
/**
 * Ndihma e pilulës vjen si `hint`, jo nga një hartë e çelësuar me ETIKETËN.
 *
 * Harta e vjetër kishte çelësa shqip (`'Punuar:'`); sapo etiketa filloi të
 * përkthehet, kërkimi do të dështonte në heshtje dhe ndihma do të zhdukej —
 * pikërisht në gjuhët ku njeriu ka më shumë nevojë për të.
 */
/** Përqindja me një decimal; null kur emëruesi është zero (s'ka bazë). */
export const pctOf = (part, whole) => (whole > 0 ? `${(Math.round((part / whole) * 1000) / 10).toLocaleString('sq-AL')}%` : null);

function StatPill({ label, value, colors, plan, hint, pct }) {
  if (!colors) colors = { bg: '#f0f0f0', fg: '#555' };
  return (
    <Box title={hint || undefined} sx={{
      display:'inline-flex', alignItems:'center', gap:.5,
      px:1, py:.35, borderRadius:'6px',
      bgcolor: colors.bg, color: colors.fg,
      fontSize:11, fontWeight:600, whiteSpace:'nowrap',
      border:`1px solid ${colors.fg}30`,
      lineHeight:1.5,
      transition:'box-shadow .15s',
      '&:hover':{ boxShadow:`0 1px 4px ${colors.fg}22` },
    }}>
      <span style={{fontWeight:500, opacity:.75, fontSize:10}}>{label}</span>
      <span style={{fontWeight:700}}>{value}</span>
      {plan != null && (
        <span style={{fontWeight:600, opacity:.6, fontSize:10}}>/ {plan}</span>
      )}
      {pct != null && (
        <span style={{fontWeight:700, fontSize:10, padding:'0 4px', borderRadius:4, background:`${colors.fg}14`}}>{pct}</span>
      )}
    </Box>
  );
}

/* ─── TIME HEADER (slot labels) ─────────────────────────────────────────────── */
/**
 * Sa hollë duhet lexuar koha, sipas asaj që është e dukshme.
 *
 * Kolonat kanë qenë gjithmonë 10-minutëshe, por vetëm ora e plotë ishte e
 * shënuar — pjesa tjetër ishin vija pa emër. Kur toleranca matet në minuta dhe
 * monitorimi ndan intervale brenda orës, "diku mes 07:00 dhe 08:00" nuk është
 * lexim; njeriu duhej t'i numëronte kolonat me gisht.
 *
 * Shkalla vjen nga HOLLËSIA E KOLONËS, jo nga zoom-i.
 *
 * Dikur varej nga sa orë ishin të dukshme: ≤4 orë → çdo kolonë, ≤9 orë → çdo
 * gjysmë ore, më gjerë → vetëm ora. Kjo e fshihte pikërisht atë që përdoruesi
 * sapo kishte zgjedhur: me kolona 10-minutëshe dhe pamje 17-orëshe, mes 06:00
 * dhe 07:00 nuk shkruhej asnjë minutë — rrjeta ndahej në gjashtë kolona pa emër.
 *
 * Kufiri i vjetër ishte edhe fizikisht i pabazë: kolona është 11px E FIKSUAR në
 * çdo zoom, dhe etiketa shkruhet e rrotulluar brenda saj. Dy etiketa nuk
 * përplasen kurrë, sado gjerë të jetë pamja — pra zoom-i s'kishte ç'të mbronte.
 *
 * Tani shkruhet çdo `labelEveryFor(slotMin)` minuta, gjithmonë e njëjta shkallë
 * për të njëjtën kolonë. Kufiri prej 10 minutash ndalon kolonat më të imëta të
 * mbushin orën me dymbëdhjetë numra; kthimi është shumëfishi i parë i kolonës që
 * e ndan orën pa mbetje, që etiketat të përsëriten njësoj në çdo orë.
 */
const LABEL_MIN_GAP = 10;   // minuta mes dy etiketave nën-orëshe

function labelEveryFor(slotMin) {
  for (let m = slotMin; m <= 60; m += slotMin) {
    if (m >= LABEL_MIN_GAP && 60 % m === 0) return m;
  }
  return 60;
}

function TimeHeader({ visibleRange, slotMin, showTeamleader, showProject, showDepartment, weeks, dayNum, nowSlot = null }) {
  const { t } = useTranslation();
  const { start, end } = visibleRange;
  const labelEvery = labelEveryFor(slotMin);
  const cols = [];
  for (let s = start; s < end; s++) {
    const min   = SLOT_START + s * slotMin;
    const hh    = Math.floor(min / 60);
    const mm    = min % 60;
    const isHour = mm === 0;
    // Ora e plotë e mban formën `HH:MM`; mes tyre shkruhet MINUTA e orës — 10,
    // 20, 30, 40, 50 — dhe pastaj ora tjetër.
    //
    // Dikur këtu përsëritej hapi (`10 10 10 10 10`), që kolonat të mos dukeshin
    // sikur zgjateshin sa më djathtas të shkoje. Por ai numër i përsëritur nuk
    // i thoshte njeriut asgjë për vendin ku ndodhej: për të gjetur 08:30 duhej
    // numëruar me gisht nga ora e fundit. Numërimi lexohet si rritje vetëm nëse
    // nuk ndalet; këtu ai RINIS te çdo orë, ndaj është cikël, jo shkallë — dhe
    // ora e plotë e mbyll ciklin dukshëm.
    //
    // Shkalla matet nga MESNATA, jo nga skaji i majtë i pamjes. Dikur ishte
    // `(s - start) % step`: etiketat vareshin nga aty ku kishte mbetur zoom-i,
    // ndaj një pamje që fillonte 04:40 i shkruante ato në :40 të çdo ore — një
    // shkallë që s'i përgjigjej asnjë ore të rrumbullakët dhe që zhvendosej sa
    // herë lëvizte pamja.
    const showLabel = isHour || min % labelEvery === 0;
    const label = !showLabel ? ''
      : isHour ? `${String(hh).padStart(2,'0')}:00`
      : String(mm);
    cols.push(
      <th key={s}
        // Shenja e përsëritur thotë sa zgjat hapi, jo ku ndodhet; ora e saktë e
        // kolonës rri këtu, që «10» të mos ketë nevojë të mbajë dy kuptime.
        title={s === nowSlot
          ? `Tani · ${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`
          : `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')} · kolona ${slotMin} min`}
        style={{
        // Vija e «tani». Nuk merr ngjyrë të vetën: statuset kanë katër, dhe një
        // e pesta do të lexohej si status i pestë. Është strukturë, ndaj mban
        // ngjyrën e vijave të rrjetës.
        boxShadow: s === nowSlot ? `inset 2px 0 0 ${G.inkStrong}` : undefined,
        width: CELL_W, minWidth: CELL_W, maxWidth: CELL_W,
        height: 42, padding: 0,
        fontSize: 8.5, fontWeight: 700,
        color: G.ink,
        textAlign: 'center',
        // Tri shkallë vijash, jo dy: ora, gjysma e orës, dhe dhjetëshet. Pa atë
        // të mesmes, gjashtë kolona të njëjta duheshin numëruar me gisht.
        borderLeft: isHour ? `2px solid ${G.lineHard}`
                  : mm === 30 ? `1px solid ${G.lineMid}`
                  : `1px solid ${G.lineSoft}`,
        background: isHour ? G.hourBg : G.head,
        boxSizing: 'border-box',
        verticalAlign: 'bottom',
        overflow: 'visible',
        position: 'relative',
      }}>
        {label && (
          <span style={{
            display: 'inline-block',
            transform: 'rotate(-90deg)',
            transformOrigin: 'center center',
            whiteSpace: 'nowrap',
            // Minutat e ndërmjetme rrinë një hap pas orës: e njëjta madhësi do
            // t'i bënte të gjitha të dukeshin si fillime ore.
            fontSize: isHour ? 8.5 : 7.5,
            fontWeight: isHour ? 700 : 500,
            color: isHour ? G.ink : G.inkFaint,
            position: 'absolute',
            bottom: 4,
            left: '50%',
            translate: '-50% 0',
          }}>
            {label}
          </span>
        )}
      </th>
    );
  }
  return (
    <tr>
      {/* sticky name col placeholder */}
      <th style={{
        position:'sticky', left:0, zIndex:5,
        width: NAME_W, minWidth: NAME_W,
        background:G.head, borderRight: (showTeamleader || showProject || showDepartment) ? `1px solid ${G.edgeStrong}` : `2px solid ${G.edgeStrong}`,
        fontSize:10.5, color:G.lineHard, fontWeight:700,
        padding:'0 10px', textAlign:'left',
        letterSpacing:'.3px',
      }}>
        {t('oret.worker')}
      </th>
      {showTeamleader && (
        <th style={{
          position:'sticky', left: NAME_W, zIndex:5,
          width: TL_COL_W, minWidth: TL_COL_W,
          background:G.head, borderRight: (showProject || showDepartment) ? `1px solid ${G.edgeStrong}` : `2px solid ${G.edgeStrong}`,
          fontSize:10.5, color:G.lineHard, fontWeight:700,
          padding:'0 8px', textAlign:'left',
        }}>{t('oret.supervisor')}</th>
      )}
      {showProject && (
        <th style={{
          position:'sticky', left: NAME_W + (showTeamleader ? TL_COL_W : 0), zIndex:5,
          width: PROJ_COL_W, minWidth: PROJ_COL_W,
          background:G.head, borderRight: showDepartment ? `1px solid ${G.edgeStrong}` : `2px solid ${G.edgeStrong}`,
          fontSize:10.5, color:G.lineHard, fontWeight:700,
          padding:'0 8px', textAlign:'left',
        }}>{t('oret.project')}</th>
      )}
      {showDepartment && (
        <th style={{
          position:'sticky', left: NAME_W + (showTeamleader ? TL_COL_W : 0) + (showProject ? PROJ_COL_W : 0), zIndex:5,
          width: DEPT_COL_W, minWidth: DEPT_COL_W,
          background:G.head, borderRight:`2px solid ${G.edgeStrong}`,
          fontSize:10.5, color:G.lineHard, fontWeight:700,
          padding:'0 8px', textAlign:'left',
        }}>{t('oret.department')}</th>
      )}
      {cols}
      {/* right totals headers — Sot */}
      {RIGHT_COL_DEFS.map((c, ci) => (
        <th key={c.key} title={t(c.titleKey)} style={{
          width: RIGHT_COL_W, minWidth: RIGHT_COL_W,
          background: ci === 0 ? '#dce8f8' : '#e8f0fb',
          fontSize: 9, fontWeight: 700,
          color: '#1a3a6b', textAlign: 'center',
          borderLeft: c.key === 'day_productive' ? '2px solid #7aadde' : `1px solid ${G.edgeStrong}`,
          padding: '0 3px', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
          letterSpacing: '0.2px',
        }}>
          {c.labelFn(dayNum || '?')}
        </th>
      ))}
      {/* weekly totals headers */}
      {(weeks || []).map(wk => (
        <th key={`wk${wk.iso_week}`} style={{
          width: RIGHT_COL_W, minWidth: RIGHT_COL_W,
          background: '#edeaf8', fontSize: 9, fontWeight: 700,
          color: '#3730a3', textAlign: 'center',
          borderLeft: '1px solid #a5b4fc',
          padding: '0 2px', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          W{wk.iso_week}
        </th>
      ))}
      {/* Grand totals */}
      {[
        // «Produktiv» mbante të njëjtin numër si «Total*», dhe «Admin» ishte
        // zero e ngurtë. Në vend të tyre hyri mungesa — e vetmja shifër e
        // vërtetë që kishte mbetur e fshehur te blloku i hequr.
        { key:'grand_total',   label:t('oret.worked'),    bg:'#E8EDF2', fg:'#243447', title:t('oret.hintGrandWorked') },
        { key:'grand_hours',   label:t('oret.plannedHours'), bg:'#E4EBF5', fg:'#26405F', title:t('oret.hintGrandPlanned') },
        { key:'grand_absence', label:t('oret.absences'),   bg:'#F6E3E3', fg:'#7A2B26', title:t('oret.hintGrandAbsence') },
        { key:'grand_diff',    label:t('oret.difference'), bg:'#EDEAE6', fg:'#4A4034', title:t('oret.hintGrandDiff') },
      ].map(c => (
        <th key={c.key} title={c.title} style={{
          width: RIGHT_COL_W + 8, minWidth: RIGHT_COL_W + 8,
          background: c.bg, fontSize: 9, fontWeight: 700,
          color: c.fg, textAlign: 'center',
          borderLeft: c.key === 'grand_total' ? '3px solid #334155' : '1px solid rgba(255,255,255,.15)',
          padding: '0 3px', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {c.label}
        </th>
      ))}
    </tr>
  );
}

/* ─── Single worker row ──────────────────────────────────────────────────────── */
/**
 * ── Matrica e ditëve ────────────────────────────────────────────────────────
 *
 * Për periudha më të gjata se një ditë, kolonat ndërrojnë kuptim: nga minuta
 * bëhen DITË. Rrjeta e minutave nuk zgjerohet dot — një javë do të ishin 1008
 * kolona — dhe as nuk duhet: pyetja ndryshon bashkë me shkallën. Për një ditë
 * pyetet «kur ishte këtu»; për një javë «si shkuan ditët».
 *
 * Tabelë e veçantë me qëllim: pamja e minutave ka rregullat e veta (plani i zi,
 * monitorimi, aktivitetet) dhe degëzimi i tyre me `if` do t'i ngatërronte të dyja.
 */
const DAY_CELL_W  = 44;
const WEEK_CELL_W = 62;

const DayMatrix = React.memo(function DayMatrix({
  workers, workerMap, columns, period, selDate, onPickDay, onOpenWorker,
}) {
  const { t } = useTranslation();
  const cellW  = period === 'quarter' ? WEEK_CELL_W : DAY_CELL_W;
  const totalW = 74;
  const width  = NAME_W + columns.length * cellW + totalW;
  const dow    = ['Die', 'Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht'];

  return (
    <Box sx={{ minWidth: width }}>
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width, fontSize: 10 }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
          <tr>
            <th style={{ position:'sticky', left:0, zIndex:5, width:NAME_W, minWidth:NAME_W,
              background:G.head, borderRight:`2px solid ${G.edgeStrong}`, borderBottom:`2px solid ${G.edge}`,
              fontSize:10.5, color:G.lineHard, fontWeight:700, padding:'0 10px', textAlign:'left' }}>
              {t('oret.worker')}
            </th>
            {columns.map((c) => {
              if (c.kind === 'week') {
                const a = new Date(c.dates[0]);
                return (
                  <th key={c.key} title={`${t('oret.week')} ${isoWeek(a)} · ${c.dates[0]} – ${c.dates[c.dates.length - 1]}`}
                    onClick={() => onPickDay(c.dates[0])}
                    style={{ width:cellW, minWidth:cellW, cursor:'pointer', background:G.head,
                      borderLeft:`1px solid ${G.edge}`, borderBottom:`2px solid ${G.edge}`,
                      padding:'2px 0', lineHeight:1.25 }}>
                    <div style={{ fontSize:8.5, color:G.inkFaint, fontWeight:600 }}>{t('oret.week')}</div>
                    <div style={{ fontSize:11, fontWeight:800, color:G.ink }}>{isoWeek(a)}</div>
                  </th>
                );
              }
              const dt = new Date(c.key);
              const weekend = dt.getDay() === 0 || dt.getDay() === 6;
              const isSel = c.key === selDate;
              return (
                <th key={c.key} title={c.key}
                  onClick={() => onPickDay(c.key)}
                  style={{ width:cellW, minWidth:cellW, cursor:'pointer',
                    background: isSel ? '#E3F0F4' : weekend ? G.zebra : G.head,
                    borderLeft:`1px solid ${G.edge}`, borderBottom:`2px solid ${G.edge}`,
                    padding:'2px 0', lineHeight:1.25 }}>
                  <div style={{ fontSize:8.5, color:G.inkFaint, fontWeight:600 }}>{dow[dt.getDay()]}</div>
                  <div style={{ fontSize:11, fontWeight:800, color: isSel ? '#1565c0' : G.ink }}>{dt.getDate()}</div>
                </th>
              );
            })}
            <th style={{ width:totalW, minWidth:totalW, background:'#E8EDF2', color:'#243447',
              fontSize:9, fontWeight:700, borderBottom:`2px solid ${G.edge}`, textAlign:'center' }}>
              Punuar
            </th>
          </tr>
        </thead>
        <tbody>
          {workers.map((w) => {
            const wid  = w.Id ?? w.id;
            const days = workerMap[wid]?.days || {};
            let sum = 0;
            return (
              <tr key={wid} style={{ borderBottom:`1px solid ${G.line}` }}>
                <td style={{ position:'sticky', left:0, zIndex:3, background:'#fff',
                  borderRight:`2px solid ${G.edgeStrong}`, padding:'2px 10px', height:ROW_H }}>
                  {/* Emri është hyrja te raporti individual — i njëjti veprim si te
                      rrjeta e minutave, që dy pamjet të mos kenë dy sjellje. */}
                  <Box component="button" onClick={() => onOpenWorker(wid)}
                    title={`${w.FirstName||w.first_name||''} ${w.LastName||w.last_name||''} — hap orarin individual`}
                    sx={{ border:'none', bgcolor:'transparent', p:0, textAlign:'left', cursor:'pointer',
                      width:'100%', fontSize:11.5, fontWeight:700, color:G.ink, whiteSpace:'nowrap',
                      overflow:'hidden', textOverflow:'ellipsis',
                      '&:hover':{ color:'#1565c0', textDecoration:'underline' } }}>
                    {(w.FirstName || w.first_name || '')} {(w.LastName || w.last_name || '')}
                  </Box>
                  <div style={{ fontSize:9, color:G.inkFaint }}>{w.EmployeeCode || w.employee_code || ''}</div>
                </td>
                {columns.map((c) => {
                  const cells = c.dates.map((d) => days[d]).filter(Boolean);
                  const min   = cells.reduce((t, d) => t + (d.net_worked_minutes || 0), 0);
                  const st    = dayStatusOf(c.kind === 'week' ? worstStatus(cells) : cells[0]?.status);
                  sum += min;
                  // Hollësia që qeliza s'e nxë dot rri te `title`: hyrja, dalja,
                  // vonesa. Për një javë, ditët që kërkojnë vëmendje.
                  const tip = c.kind === 'week'
                    ? `${c.dates[0]} – ${c.dates[c.dates.length - 1]} · ${fmtH(min)}\n` +
                      cells.filter((d) => d.status === 'absent' || d.late_minutes > 0)
                           .map((d) => `${d.status === 'absent' ? 'Mungesë' : `Vonesë ${d.late_minutes}m`}`)
                           .join(' · ')
                    : cells[0]
                      ? [`${c.key} · ${t(st.key)}`,
                         cells[0].check_in ? `Hyrje ${cells[0].check_in}` : null,
                         cells[0].check_out ? `Dalje ${cells[0].check_out}` : null,
                         min ? `Punuar ${fmtH(min)}` : null,
                         cells[0].break_minutes ? `Pauzë ${cells[0].break_minutes}m` : null,
                         cells[0].late_minutes > 0 ? `Vonesë ${cells[0].late_minutes}m` : null,
                         cells[0].overtime_minutes > 0 ? `Overtime ${cells[0].overtime_minutes}m` : null,
                         cells[0].leave?.Label ? `Pushim: ${cells[0].leave.Label}` : null,
                        ].filter(Boolean).join(' · ')
                      : c.key;
                  return (
                    <td key={c.key} onClick={() => onPickDay(c.dates[0])} title={tip}
                      style={{ width:cellW, minWidth:cellW, height:ROW_H, cursor:'pointer',
                        background: st.bg, color: st.fg, textAlign:'center',
                        fontSize:9.5, fontWeight:700, padding:0,
                        borderLeft: c.key === selDate ? '2px solid #1565c0' : `1px solid ${G.line}` }}>
                      {min ? fmtH(min) : ''}
                    </td>
                  );
                })}
                <td className="tabular" style={{ width:totalW, minWidth:totalW, height:ROW_H,
                  background:'#E8EDF2', color:'#243447', textAlign:'center',
                  fontSize:10.5, fontWeight:800 }}>
                  {fmtH(sum)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
});

const WorkerRow = React.memo(function WorkerRow({
  // `dayDate`, `selected`, `onSelect` dhe `tolerance` kalonin nga prindi dhe
  // nuk lexoheshin kurrë këtu: përzgjedhja bëhet me `gridSelected`, dhe
  // toleranca zbatohet te serveri. Props të vdekur mbi një komponent `memo`
  // s'janë vetëm rrëmujë — ata e rikrijojnë atë sa herë ndryshon vlera e tyre.
  worker, dayData, slots, visibleRange, slotMin, nowSlot,
  showTeamleader, showProject, workerProject, showDepartment, workerDept,
  overviewWorker, weeks, selDate,
  gridSelected, onGridSelect, showMonitoring, onOpenSchedule,
}) {
  const { t } = useTranslation();
  const { start, end } = visibleRange;

  /* totals from dayData */
  const netMin   = dayData?.net_worked_minutes ?? 0;

  const rowBg = gridSelected ? '#c8e6c9' : 'transparent';

  const wid = worker.Id ?? worker.id;
  const workerName = `${worker.FirstName||worker.first_name||''} ${worker.LastName||worker.last_name||''}`.trim();

  return (
    <tr
      data-wid={wid}
      style={{ height: ROW_H, background: rowBg, cursor: 'pointer', borderBottom:`1px solid ${G.line}`, borderLeft: gridSelected ? '3px solid #388e3c' : '3px solid transparent', outline: gridSelected ? '0' : 'none' }}
      onClick={e => onGridSelect && onGridSelect(wid, e.shiftKey)}
      onMouseEnter={e => { if (!gridSelected) e.currentTarget.style.background = G.hover; }}
      onMouseLeave={e => { if (!gridSelected) e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Worker name — sticky */}
      <td style={{
        position:'sticky', left:0, zIndex:2,
        width: NAME_W, minWidth: NAME_W,
        background: gridSelected ? '#c8e6c9' : '#fff',
        borderRight: (showTeamleader || showProject || showDepartment) ? `1px solid ${G.edge}` : `2px solid ${G.edge}`,
        padding:'0 6px 0 8px', fontSize:11.5, fontWeight:600, color:G.ink,
        boxSizing:'border-box',
        borderBottom:`1px solid ${G.line}`,
      }}>
        {/**
         * Emri zë një rresht të tijin, kodi dhe orari rreshtin poshtë.
         *
         * Të tria rrinin në një rresht brenda 190px: kodi merrte rreth 85,
         * chip-i i orarit rreth 75, dhe emrit i mbeteshin tridhjetë — «Fisni…».
         * Pikërisht emri është ajo me të cilën kërkohet rreshti; kodi dhe orari
         * lexohen vetëm pasi rreshti është gjetur, ndaj ata dorëzojnë gjerësinë,
         * jo ai.
         */}
        <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:1, width:'100%', overflow:'hidden' }}>
          <span
            role="button"
            tabIndex={0}
            title={`${workerName} — hap Orarin Individual Mujor (vetëm shikim)`}
            onClick={(e) => { e.stopPropagation(); onOpenSchedule && onOpenSchedule(wid); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onOpenSchedule && onOpenSchedule(wid); }
            }}
            style={{
              minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              fontSize:11.5, fontWeight:600, color:G.ink, lineHeight:'13px',
              cursor: onOpenSchedule ? 'pointer' : 'inherit',
              textDecoration: onOpenSchedule ? 'underline dotted transparent' : 'none',
            }}
            onMouseEnter={(e) => { if (onOpenSchedule) e.currentTarget.style.textDecorationColor = '#5b7fa6'; }}
            onMouseLeave={(e) => { if (onOpenSchedule) e.currentTarget.style.textDecorationColor = 'transparent'; }}
          >
            {workerName}
          </span>
          <div style={{ display:'flex', alignItems:'center', gap:4, width:'100%', overflow:'hidden', lineHeight:'12px' }}>
            <span title="Kodi i punonjësit" style={{ fontSize:9, color:G.inkFaint, flexShrink:0 }}>
            {worker.EmployeeCode || worker.employee_code || ''}
          </span>
          {overviewWorker?.schedule_start ? (
            <span title="Orari i caktuar" style={{
              flexShrink:0,
              fontSize:9.5, fontWeight:700, fontVariantNumeric:'tabular-nums',
              color: gridSelected ? '#2e7d32' : '#3d8fa3',
              background: gridSelected ? 'rgba(46,125,50,.12)' : 'rgba(61,143,163,.12)',
              borderRadius:4, padding:'1px 5px',
              whiteSpace:'nowrap', userSelect:'none',
            }}>
              {String(overviewWorker.schedule_start).slice(0,5)}–{String(overviewWorker.schedule_end||'').slice(0,5)}
            </span>
          ) : (
            <span title="Pa orar të caktuar" style={{
              flexShrink:0, fontSize:9, fontWeight:600,
              color:'#b0900a', background:'rgba(224,161,0,.14)',
              borderRadius:4, padding:'1px 5px', whiteSpace:'nowrap', userSelect:'none',
            }}>
              {t('oret.noSchedule')}
            </span>
          )}
          {/* Shenja e lejes rri PRANË orarit, jo në vend të tij: rasti që duhet
              parë është pikërisht «kishte orar, dhe atë ditë ishte me leje». */}
          {dayData?.leave && (
            <span title={`${dayData.leave.Label} — pushim i aprovuar`} style={{
              flexShrink:0, fontSize:9, fontWeight:700,
              color: leaveTint(dayData.leave.TypeId),
              background: leaveBg(dayData.leave.TypeId),
              borderRadius:4, padding:'1px 5px', whiteSpace:'nowrap', userSelect:'none',
            }}>
              {dayData.leave.Label}{dayData.leave.HalfDay ? ' ½' : ''}
            </span>
          )}
          </div>
        </div>
      </td>
      {showTeamleader && (
        <td style={{
          position:'sticky', left: NAME_W, zIndex:2,
          width: TL_COL_W, minWidth: TL_COL_W,
          background: gridSelected ? '#c8e6c9' : '#fff',
          borderRight: (showProject || showDepartment) ? `1px solid ${G.edge}` : `2px solid ${G.edge}`,
          padding:'0 8px', fontSize:10.5, color:G.inkMid,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          boxSizing:'border-box', borderBottom:`1px solid ${G.line}`,
        }}>
          {worker.SupervisorName || worker.supervisor_name || '—'}
        </td>
      )}
      {showProject && (
        <td style={{
          position:'sticky', left: NAME_W + (showTeamleader ? TL_COL_W : 0), zIndex:2,
          width: PROJ_COL_W, minWidth: PROJ_COL_W,
          background: gridSelected ? '#c8e6c9' : '#fff',
          borderRight: showDepartment ? `1px solid ${G.edge}` : `2px solid ${G.edge}`,
          padding:'0 8px', fontSize:10.5, color:G.inkMid,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          boxSizing:'border-box', borderBottom:`1px solid ${G.line}`,
        }}>
          {workerProject || '—'}
        </td>
      )}
      {showDepartment && (
        <td style={{
          position:'sticky', left: NAME_W + (showTeamleader ? TL_COL_W : 0) + (showProject ? PROJ_COL_W : 0), zIndex:2,
          width: DEPT_COL_W, minWidth: DEPT_COL_W,
          background: gridSelected ? '#c8e6c9' : '#fff',
          borderRight:`2px solid ${G.edge}`,
          padding:'0 8px', fontSize:10.5, color:G.inkMid,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          boxSizing:'border-box', borderBottom:`1px solid ${G.line}`,
        }}>
          {workerDept || '—'}
        </td>
      )}

      {/* Time slot cells */}
      {(() => {
        const planStart = dayData?.schedule_start ? Math.floor(toMin(dayData.schedule_start) / slotMin) : null;
        const planEnd   = dayData?.schedule_end   ? Math.ceil(toMin(dayData.schedule_end)   / slotMin) : null;
        /**
         * Dita me tip të ARSYETUAR (sëmundje, pushim, leje pa pagesë) — nga
         * fjalori, jo nga tabela e lejeve. Serveri s'i dërgon `monitor` as asaj,
         * dhe shiriti i planit merr ngjyrën e vetë tipit në vend të së zezës.
         */
        const excusedWT = dayData?.working_type_excused ? dayData : null;
        // Plani i tipizuar — i pavarur nga monitorimi: ai thotë çfarë NDODHI,
        // ky çfarë ISHTE CAKTUAR, dhe të dyja duhen njëkohësisht.
        const planCells = excusedWT ? null : buildPlanSlots(dayData?.plan, slotMin);
        // Segmentet vijnë nga serveri; kur monitorimi është i fikur, rrjeta
        // mbetet saktësisht ajo që ishte — një shirit i zi mbi orarin e planit.
        // Serveri s'e dërgon `monitor` për një ditë me leje; kjo është rojtari i
        // dytë, që edhe një përgjigje e vjetër e ruajtur të mos e ngjyrosë kuq.
        const monCells = showMonitoring && !dayData?.leave
          ? buildMonitorSlots(dayData?.monitor, slotMin) : null;
        // Aktiviteti rri MBI prezencën: «ishte aty» dhe «çfarë bënte» janë dy
        // pyetje, dhe e dyta është ajo që kërkohet kur e para ka përgjigje.
        const actCells = showMonitoring && !dayData?.leave
          ? buildActivitySlots(dayData?.activities, slotMin) : null;
        /**
         * ── ETIKETA NJË PËR BLLOK, JO NJË PËR KOLONË ────────────────────────
         *
         * «P P P P P P P P» pesëdhjetë herë radhazi s'është etiketë, është teksturë.
         * Asnjë grid orari profesional s'e bën: emri i bllokut shkruhet një herë,
         * në mes të tij. Dhe kur shkruhet një herë, ka vend për një fjalë të vërtetë
         * — «Pushim vjetor» — në vend të një shkronje që përplaset me tjetrën.
         *
         * Përplasja ishte e vërtetë: `Productive Hours` dhe `Pushim vjetor` jepnin
         * të dyja «P», dhe ngjyra mbetej i vetmi dallim — pikërisht ajo që s'duhet
         * ta mbajë kuptimin vetëm.
         *
         * Teksti llogaritet KËTU, një herë për qelizë, dhe qeliza vetëm e lexon:
         * dy vende që e nxjerrin do të pajtoheshin derisa njëri të ndryshonte.
         */
        const slotText = (s) => {
          const typ0 = slots[s] ?? 'empty';
          const mon0 = monCells ? monCells[s] : null;
          const act0 = actCells ? actCells[s] : null;
          const actPaint0 = act0 && mon0?.state === 'covered' ? activityOf(act0.type) : null;
          const paint0 = actPaint0 || (mon0 ? monitorPaint(mon0) : null);
          const inPlan0 = monCells ? !!mon0
            : (planStart != null && planEnd != null && s >= planStart && s < planEnd);
          const pp = planCells ? planCells[s] : null;
          if (pp && pp.kind === 'break') return breakSymbol(pp.sub);
          if (paint0) return paint0.mark;
          if (inPlan0 && excusedWT) return excusedWT.working_type_name || '';
          if (pp) return pp.name;
          return SLOT_LABELS[typ0] || '';
        };

        // Vrapimet: kolona fqinje me të njëjtin tekst janë një bllok i vetëm.
        const texts = Array.from({ length: end - start }, (_, i) => slotText(start + i));
        const labelAt = new Map();
        for (let i = 0; i < texts.length; ) {
          const t = texts[i];
          let j = i; while (j < texts.length && texts[j] === t) j++;
          if (t) labelAt.set(start + ((i + j - 1) >> 1), { text: t, run: j - i });
          i = j;
        }

        return Array.from({ length: end - start }, (_, i) => {
        const s   = start + i;
        const typ = slots[s] ?? 'empty';
        const mon = monCells ? monCells[s] : null;
        const act = actCells ? actCells[s] : null;
        // Aktiviteti ngjyros vetëm aty ku ka prezencë: një mbledhje e shënuar
        // jashtë turnit s'ka çfarë të mbulojë.
        const actPaint = act && mon?.state === 'covered' ? activityOf(act.type) : null;
        const paint = actPaint || (mon ? monitorPaint(mon) : null);
        // Me monitorim, "brenda planit" e thotë vetë segmenti: vetëm ai e di
        // se ku shtrihet një turn nate që kalon mesnatën.
        const inPlan = monCells ? !!mon
                     : (planStart != null && planEnd != null && s >= planStart && s < planEnd);
        const isPlanStart = inPlan && (monCells ? !monCells[s - 1] : s === planStart);
        const isPlanEnd   = inPlan && (monCells ? !monCells[s + 1] : s === planEnd - 1);
        const onLeave = typ === 'leave' ? dayData?.leave : null;
        const base = gridSelected && typ === 'empty' ? '#a5d6a7'
                   : onLeave ? leaveBg(onLeave.TypeId)
                   : (SLOT_COLORS[typ] || SLOT_COLORS.empty);
        // The whole planned window renders as a solid black band so the schedule
        // is unmistakable; activity codes stay readable in white on top.
        //
        // PËRJASHTIM: një ditë me tip të arsyetuar (sëmundje, pushim) s'ka
        // monitorim — pra shiriti do të mbetej i zi, sikur plani të mos ishte
        // prekur kurrë. Ai merr ngjyrën e VETË tipit, atë që e mban fjalori,
        // dhe jo një ngjyrë të pestë statusi.
        const planPart = planCells ? planCells[s] : null;
        /**
         * Plani dhe monitorimi janë DY KANALE, jo dy radhë të së njëjtës.
         *
         * Fillimisht plani u vu nën monitorimin, dhe rezultati ishte se s'dukej
         * kurrë: me monitorimin ndezur dhe askënd të ardhur, e kuqja e mbulonte
         * tërë ditën. Por dy nga ato shtresa nuk konkurrojnë fare:
         *
         *  · **Pauza e planifikuar FITON mbi monitorimin.** Jo për estetikë — një
         *    drekë e caktuar nuk është mungesë, dhe e kuqja mbi të thotë «duhej
         *    të kishte punuar» për një orë që vetë kompania e ka hequr nga puna.
         *  · **Lloji i punës nuk konkurron: rri te SKAJI.** Ngjyra e mbushjes
         *    mbetet e monitorimit (a ishte këtu), kurse një vijë lart thotë çfarë
         *    ishte caktuar. Dy pyetje, dy vende — jo dy ngjyra që luftojnë.
         */
        const planBreak = planPart && planPart.kind === 'break' ? planPart : null;
        const planWork  = planPart && planPart.kind === 'work'  ? planPart : null;
        const bg  = planBreak ? BREAK_BG
                  : paint ? paint.bg
                  : excusedWT && inPlan ? excusedBg(excusedWT.working_type_color)
                  : planWork ? `${planWork.color}33`
                  : inPlan ? PLAN_BAND_BG
                  : base;
        const min = SLOT_START + s * slotMin;
        const mm = min % 60;
        const isHour = mm === 0;
        // Shenja e monitorimit fiton mbi kodin e aktivitetit: kur pyetja është
        // "a ishte këtu apo jo", kodi i aktivitetit nuk i përgjigjet asaj.
        /**
         * Etiketa e bllokut — e plotë kur ka vend, e shkurtuar kur jo.
         *
         * Gjerësia e bllokut vendos: «Pushim vjetor» kërkon rreth 70px, dhe një
         * pauzë 10-minutëshe ka njëmbëdhjetë. Prandaj tri shkallë, jo një:
         * emri i plotë → inicialet e fjalëve → asgjë. Kurrë një shkronjë e vetme
         * e nxjerrë me hamendje, se pikërisht ajo i barazoi dy tipa të ndryshëm.
         */
        const lab = labelAt.get(s);
        const label = (() => {
          if (!lab) return '';
          const room = lab.run * CELL_W - 3;
          if (lab.text.length <= 2) return lab.text;             // shenjat: ✓ ! ~
          if (lab.text.length * 5.4 <= room) return lab.text;    // emri i plotë
          const short = lab.text.trim().split(/[\s·/-]+/).filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase()).join('').slice(0, 3);
          return short.length * 5.4 <= room ? short : '';
        })();

        return (
          <td key={s}
            // Kapëse për zgjedhjen me tërheqje: delegimi te kontejneri i lexon
            // këto, në vend që 1944 qeliza të mbanin secila mbajtësin e vet.
            data-slot={s}
            title={onLeave
              ? `${onLeave.Label}${onLeave.HalfDay ? ' (gjysmë dite)' : ''} — pushim i aprovuar`
              : (inPlan && excusedWT && !paint)
              ? `${excusedWT.working_type_name} — mungesë e arsyetuar, jo mungesë`
              : (planPart && !paint)
              ? `Planifikuar: ${planPart.label} · ${fmtClock(planPart.from)}–${fmtClock(planPart.to)}`
              : actPaint
              ? `${t(actPaint.key)} · ${fmtClock(act.from)}–${fmtClock(act.to)}`
              : mon
              ? `${mon.stateLabel}${mon.tolerated ? ' (brenda tolerancës)' : ''} · ${mon.label}${mon.nextDay ? ' (të nesërmen)' : ''}`
              : undefined}
            style={{
            width: CELL_W, minWidth: CELL_W, maxWidth: CELL_W,
            background: bg,
            // Black scheduled band with a white outline: top/bottom accents run the
            // length of the planned window; left/right caps close it into a lane,
            // and inner hour lines stay faint white so they read on the black fill.
            borderTop: inPlan ? `2px solid ${paint ? G.inkStrong : PLAN_BAND_BORDER}` : undefined,
            borderLeft: isPlanStart ? `2px solid ${paint ? G.inkStrong : PLAN_BAND_BORDER}`
                      : inPlan ? (paint
                          ? (isHour ? '1px solid rgba(0,0,0,0.28)' : '1px solid rgba(0,0,0,0.10)')
                          : (isHour ? '1px solid rgba(255,255,255,0.45)' : '1px solid rgba(255,255,255,0.15)'))
                      : isHour ? `2px solid ${G.lineHard}`
                      : mm === 30 ? `1px solid ${G.lineMid}`
                      : `1px solid ${G.line}`,
            borderRight: isPlanEnd ? `2px solid ${paint ? G.inkStrong : PLAN_BAND_BORDER}` : undefined,
            borderBottom: inPlan ? `2px solid ${paint ? G.inkStrong : PLAN_BAND_BORDER}` : `1px solid ${G.line}`,
            padding: 0, height: ROW_H,
            // Etiketa e bllokut është më e gjerë se qeliza që e mban; `relative`
            // + `overflow: visible` e lënë të shtrihet mbi fqinjët e SAJ, që janë
            // të njëjtit bllok e të njëjtës ngjyrë.
            position: label && label.length > 2 ? 'relative' : undefined,
            overflow: label && label.length > 2 ? 'visible' : undefined,
            // Dy theksa të brendshëm, të pavarur, në një varg:
            //
            //  · Vija e «tani» — majtas. S'merr ngjyrë të vetën: statuset kanë
            //    katër, dhe një e pesta do të lexohej si status i pestë.
            //  · Lloji i planifikuar i punës — LART, 3px. Rri te skaji pikërisht
            //    që të mos konkurrojë me mbushjen: ajo i përgjigjet pyetjes «a
            //    ishte këtu», kjo pyetjes «çfarë ishte caktuar». Dy pyetje, dy
            //    vende. Pa këtë, monitorimi i ndezur e fshihte planin tërësisht.
            boxShadow: [
              s === nowSlot
                ? `inset 2px 0 0 ${inPlan && !paint ? PLAN_BAND_BORDER : G.inkStrong}` : null,
              planWork ? `inset 0 3px 0 ${planWork.color}` : null,
            ].filter(Boolean).join(', ') || undefined,
            boxSizing:'border-box',
            textAlign:'center',
            // Simbolet janë glife, jo shkronja: te 7.5px një filxhan bëhet njollë.
            fontSize: 7.5,
            fontWeight: paint ? 800 : 600,
            color: onLeave ? leaveTint(onLeave.TypeId)
                 : paint ? paint.fg
                 : (inPlan && excusedWT) ? excusedInk(excusedWT.working_type_color)
                 // Pauza është tint i lehtë → shkrim në ngjyrën e plotë; puna
                 // është e ngopur → shkrim i bardhë.
                 : planBreak ? BREAK_INK
                 : planWork ? planWork.color
                 : inPlan ? PLAN_BAND_TEXT
                 : typ === 'work' ? '#2e7d32' : typ === 'overtime' ? '#e65100' : typ === 'admin' ? '#6a1b9a' : typ === 'absent' ? '#c62828' : '#bbb',
            lineHeight: `${ROW_H}px`,
          }}>
            {isBreakToken(label) ? <BreakIcon token={label} />
             : label && label.length > 2 ? (
              <span style={{ position:'absolute', left:'50%', top:0,
                transform:'translateX(-50%)', whiteSpace:'nowrap',
                lineHeight:`${ROW_H}px`, pointerEvents:'none' }}>
                {label}
              </span>
            ) : label}
          </td>
        );
      });
      })()}

      {/* Right totals */}
      {(() => {
        // `net_worked_minutes` vjen tashmë NETO nga serveri; zbritja e pauzës
        // këtu e bënte «Produktive» të tregonte punë − pauzë − pauzë, dhe
        // rreshti nuk pajtohej me pilulën me të njëjtin emër.
        const prodMin  = Math.max(0, netMin);
        const dayTotal = netMin;
        // Cumulative "till day" — sum net_worked_minutes for all days up to & including selDate
        let tillDay = 0;
        if (overviewWorker?.days) {
          for (const [dt, dd] of Object.entries(overviewWorker.days)) {
            if (dt <= selDate) tillDay += dd.net_worked_minutes || 0;
          }
        }
        const dayVals = [
          { key: 'day_productive', v: prodMin },
          { key: 'day_total',      v: dayTotal },
          { key: 'till_day',       v: tillDay },
        ];
        return dayVals.map(({ key, v }, ci) => (
          <td key={key} style={{
            width: RIGHT_COL_W, minWidth: RIGHT_COL_W,
            textAlign: 'center', fontSize: 9, fontWeight: 600,
            color: ci === 0 ? '#1a3a6b' : G.inkStrong,
            borderLeft: key === 'day_productive' ? '2px solid #7aadde' : `1px solid ${G.edgeStrong}`,
            background: gridSelected ? (ci === 0 ? '#81c784' : '#a5d6a7') : (ci === 0 ? '#dce8f8' : ci % 2 === 0 ? '#f0f5fc' : '#e8f0fb'),
            padding: '0 3px',
          }}>
            {fmtH(v)}
          </td>
        ));
      })()}
      {/* Weekly totals */}
      {(weeks || []).map(wk => {
        const wt = overviewWorker?.weekly_totals?.[wk.iso_week];
        const worked = wt?.worked || 0;
        return (
          <td key={`wk${wk.iso_week}`} style={{
            width: RIGHT_COL_W, minWidth: RIGHT_COL_W,
            textAlign: 'center', fontSize: 9, fontWeight: 600,
            color: '#3730a3', borderLeft: '1px solid #a5b4fc',
            background: '#f5f3ff',
            padding: '0 3px',
          }}>
            {fmtH(worked)}
          </td>
        );
      })}
      {/* Grand totals */}
      {(() => {
        const tw = overviewWorker?.total_worked || 0;
        const tp = overviewWorker?.total_planned || 0;
        const absent = overviewWorker?.hour_distribution?.absence || 0;
        const diff = tw - tp;
        return [
          { key:'grand_total',   v: tw,   bg:'#E8EDF2', fg:'#243447' },
          { key:'grand_hours',   v: tp,   bg:'#E4EBF5', fg:'#26405F' },
          { key:'grand_absence', v: absent, bg:'#F6E3E3', fg:'#7A2B26' },
          { key:'grand_diff',    v: diff, bg: diff >= 0 ? '#14532d' : '#F6E3E3', fg: diff >= 0 ? '#bbf7d0' : '#7A2B26' },
        ].map(({ key, v, bg, fg }) => (
          <td key={key} style={{
            width: RIGHT_COL_W + 8, minWidth: RIGHT_COL_W + 8,
            textAlign: 'center', fontSize: 9, fontWeight: 700,
            color: fg || '#243447',
            borderLeft: key === 'grand_total' ? '3px solid #334155' : '1px solid rgba(255,255,255,.15)',
            background: bg || '#E8EDF2',
            padding: '0 3px',
          }}>
            {key === 'grand_diff' && v > 0 ? `+${fmtH(v)}` : fmtH(v)}
          </td>
        ));
      })()}
    </tr>
  );
});

/* ─── Main page ─────────────────────────────────────────────────────────────── */
export default function OretPage() {
  const { t } = useTranslation();
  const { hasAnyPermission } = usePermissions();
  const canEditSchedule = hasAnyPermission(['attendance.manage', 'settings.manage']);
  /* ── State ── */
  const now = new Date();
  const [selDate, setSelDate] = useState(TODAY);
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [showProject,    setShowProject]    = useState(false);
  const [showDepartment, setShowDepartment] = useState(false);
  const [showTeamleader, setShowTeamleader] = useState(false);
  const [showMonitoring, setShowMonitoring] = useState(true);
  const [tolerance,      setTolerance]      = useState(0);
  // Toleranca zbatohet në server, ndaj çdo prekje e saj është një kërkesë e re.
  // Vlera e vonuar e mban shiritin të gjallë ndërsa gishtat shkruajnë: pa të,
  // "15" do të nisë tri kërkesa — për 1, për 15 dhe për çfarëdo mes tyre.
  const [tolApplied,     setTolApplied]     = useState(0);
  useEffect(() => {
    // Tolerancë negative s'ka kuptim — shkrimi i «-» s'e ndryshon ndarjen.
    const id = setTimeout(() => setTolApplied(Math.max(0, Number(tolerance) || 0)), 400);
    return () => clearTimeout(id);
  }, [tolerance]);
  // Periudha ruhet si mënyrë shikimi (si `slotMin`): dy veta mbi të njëjtin
  // llogari mund ta duan ndryshe, dhe s'është e dhënë e kompanisë.
  const [period, setPeriod] = useState(() => {
    try { const v = localStorage.getItem('oret.period'); return PERIODS.some(p => p.key === v) ? v : 'day'; }
    catch { return 'day'; }
  });
  const changePeriod = (next) => {
    setPeriod(next);
    try { localStorage.setItem('oret.period', next); } catch {}
  };
  const [search,         setSearch]         = useState('');
  // `?project=ID` — klikimi mbi një projekt te «Plani i Punës» hap orët e tij.
  const location = useLocation();
  const urlProject = new URLSearchParams(location.search).get('project') || '';
  const [selProject,     setSelProject]     = useState(urlProject);
  // A e ka prekur njeriu filtrin e projektit — parazgjedhja s'e mbivendos zgjedhjen.
  const projectTouchedRef = useRef(Boolean(urlProject));
  useEffect(() => { if (urlProject) setSelProject(urlProject); }, [urlProject]);

  /* grid multi-select (for quick schedule assign) */
  const [gridSelectedWorkers, setGridSelectedWorkers] = useState(new Set());

  /**
   * ── ZGJEDHJA ME TËRHEQJE ──────────────────────────────────────────────────
   *
   * Si te Excel-i: shtyp mbi një qelizë, tërhiq, lësho. Zgjedhja që del s'është
   * vetëm një listë punëtorësh — është një DRITARE: kush, dhe nga sa deri sa.
   * Pikërisht ajo dritare i mungonte formës, e cila e merrte orarin gjithmonë
   * nga shablloni dhe ta linte njeriut ta rishkruante me dorë.
   *
   * Mbajtësit rrinë te KONTEJNERI, jo te qelizat: 27 rreshta × 72 kolona janë
   * 1944 qeliza, dhe një mbajtës për secilën do të krijonte dy mijë mbyllje në
   * çdo rirender. Delegimi i lexon `data-wid`/`data-slot` nga ngjarja.
   *
   * Drejtkëndëshi vizatohet nga RECT-et e vërteta të dy qelizave, jo nga
   * aritmetika e kolonave: koka ka lartësi të ndryshueshme, rrjeta rrëshqet, dhe
   * çdo llogaritje e dytë e pozicionit do të shkonte jashtë sinkronit me të parën.
   */
  const [drag, setDrag] = useState(null);      // { a:{wid,slot,el}, b:{...} }
  const [dragBox, setDragBox] = useState(null); // { left, top, width, height }
  const gridRef = useRef(null);
  const dragging = useRef(false);
  const suppressRowClickRef = useRef(false);

  const cellAt = (target) => {
    const td = target?.closest?.('td[data-slot]');
    const tr = td?.closest?.('tr[data-wid]');
    if (!td || !tr) return null;
    return { wid: Number(tr.getAttribute('data-wid')), slot: Number(td.getAttribute('data-slot')), el: td };
  };

  const boxOf = (a, b) => {
    const host = gridRef.current;
    if (!host || !a?.el || !b?.el) return null;
    const h = host.getBoundingClientRect();
    const ra = a.el.getBoundingClientRect();
    const rb = b.el.getBoundingClientRect();
    const left = Math.min(ra.left, rb.left) - h.left + host.scrollLeft;
    const top  = Math.min(ra.top,  rb.top)  - h.top  + host.scrollTop;
    return { left, top,
      width:  Math.max(ra.right, rb.right) - Math.min(ra.left, rb.left),
      height: Math.max(ra.bottom, rb.bottom) - Math.min(ra.top, rb.top) };
  };

  const onGridMouseDown = (e) => {
    if (e.button !== 0) return;
    const c = cellAt(e.target);
    if (!c) return;
    // Pa këtë, tërheqja e zgjedh tekstin e rrjetës dhe drejtkëndëshi humbet nën të.
    e.preventDefault();
    dragging.current = true;
    setDrag({ a: c, b: c });
    setDragBox(boxOf(c, c));
  };

  const onGridMouseMove = (e) => {
    if (!dragging.current) return;
    const c = cellAt(e.target);
    if (!c) return;
    setDrag((d) => (d ? { ...d, b: c } : d));
    setDragBox(boxOf(drag?.a || c, c));
  };

  const onGridMouseUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setDragBox(null);
    const d = drag;
    setDrag(null);
    if (!d) return;

    // Rreshtat mes dy skajeve — sipas RENDIT në ekran, jo sipas id-së.
    const order = filteredWorkers.map((w) => w.Id ?? w.id);
    const ia = order.indexOf(d.a.wid), ib = order.indexOf(d.b.wid);
    if (ia < 0 || ib < 0) return;
    const picked = order.slice(Math.min(ia, ib), Math.max(ia, ib) + 1);

    const s0 = Math.min(d.a.slot, d.b.slot);
    const s1 = Math.max(d.a.slot, d.b.slot);
    const from = fmtClock(SLOT_START + s0 * slotMin);
    // Fundi është skaji i DJATHTË i kolonës së fundit: një tërheqje mbi një
    // kolonë të vetme është dhjetë minuta, jo zero.
    const to   = fmtClock(SLOT_START + (s1 + 1) * slotMin);

    setGridSelectedWorkers(new Set(picked));
    lastClickedIdxRef.current = null;
    // Vetëm një TËRHEQJE e vërtetë cakton dritare orësh. Më parë mjaftonte një
    // klikim i vetëm mbi një qelizë: dritarja bëhej 08:00–08:10, dhe caktimi
    // krijonte një orar të ri me atë emër.
    setDragWindow(s1 > s0 ? { from, to } : null);
    // Klikimi që ndjek mouseup-in mbi të njëjtin rresht do ta çzgjidhte sërish
    // punëtorin që sapo u zgjodh; rreshti e shpërfill atë klikim të vetëm.
    suppressRowClickRef.current = true;
  };

  /**
   * Dritarja e tërhequr fiton mbi orët e shabllonit.
   *
   * Efekti që mbush orët nga orari i zgjedhur xhiron sa herë ndryshon
   * `quickScheduleId` — dhe ai ndryshon pikërisht kur zgjidhen punëtorë. Pa një
   * gjendje të veçantë, ajo do t'i mbishkruante orët që sapo tërhoqi njeriu.
   */
  const [dragWindow, setDragWindow] = useState(null);
  const [allGridSchedules,    setAllGridSchedules]    = useState([]);
  const [quickScheduleId,     setQuickScheduleId]     = useState('');
  const [quickAssigning,      setQuickAssigning]      = useState(false);
  const [quickSnack,          setQuickSnack]          = useState('');
  const [quickEffectiveDate,  setQuickEffectiveDate]  = useState('');
  const [quickShortBreaks,    setQuickShortBreaks]    = useState([]);  // [{id,min,time,working_type_code}]
  // Lloji i pauzës së orarit: një numër i vetëm te serveri, ndaj një gjendje e vetme këtu.
  const [quickLongBreakType,  setQuickLongBreakType]  = useState('');
  const [quickLongBreakStart, setQuickLongBreakStart] = useState('');
  const [quickEffectiveTo,    setQuickEffectiveTo]    = useState('');
  const [quickStartTime,      setQuickStartTime]      = useState('');
  const [quickEndTime,        setQuickEndTime]        = useState('');
  const [quickBreakSched,     setQuickBreakSched]     = useState('');
  const defaultScheduleId = useMemo(() => {
    if (quickScheduleId) return quickScheduleId;
    const def = allGridSchedules.find((s) => s.is_default) || allGridSchedules[0];
    return def ? String(def.id ?? def.Id) : '';
  }, [quickScheduleId, allGridSchedules]);
  // Project hour-split + department for the assignment
  // Forma e plotë e caktimit. Shiriti mbetet për rrugën e shpejtë; kur duhet
  // vendosur diçka (departament, ndarje orësh, pauza), hapet ky ekran.
  const [allocOpen,           setAllocOpen]           = useState(false);

  const [quickDescription,    setQuickDescription]    = useState('');
  const [quickDepartment,     setQuickDepartment]     = useState('');
  const [quickProjectSegs,    setQuickProjectSegs]    = useState([]); // [{id, project_id, start, end}]
  const lastClickedIdxRef = useRef(null);

  const [workers,  setWorkers]  = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [overview, setOverview] = useState(null); // { workers[], days[], weeks[] }
  const [loading,  setLoading]  = useState(false);
  const [workersLoading, setWorkersLoading] = useState(true);
  const [workersError,   setWorkersError]   = useState(null); // { type: 'auth'|'http'|'network'|'empty', detail }

  /* load schedules for quick-assign bar */
  useEffect(() => {
    fetch(`${API}/api/workSchedules`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(j => setAllGridSchedules(j.data || []))
      .catch(() => {});
  }, []);

  /**
   * Departamenti dhe projekti, ashtu si i mban PROFILI i punëtorit.
   *
   * `workers.department_id` dhe `workers.project_ids` janë profili — jo
   * `worker_project_assignments`, që është histori caktimesh me data dhe do të
   * thoshte "ku ka punuar", jo "ku bën pjesë".
   *
   * KUR ZGJEDHJA NUK PAJTOHET, NUK MERRET ME MEND. Nëse zgjidhen pesë punëtorë
   * nga tri departamente, mbushja me njërin do t'ua caktonte të gjithëve atë
   * departament pa e vënë re askush — dhe caktimi shkon te secili veç e veç.
   * Në atë rast fusha mbetet bosh dhe njeriu vendos.
   */
  const profileDefaults = useMemo(() => {
    const sel = workers.filter(w => gridSelectedWorkers.has(w.Id ?? w.id));
    if (!sel.length) return { departmentId: '', projectId: '' };

    // Vlera e përbashkët e të gjithëve, ose asgjë. Një i vetëm pa vlerë
    // mjafton për të mos pasur "të përbashkët".
    const agreed = (pick) => {
      const vals = sel.map((w) => {
        const v = pick(w);
        return v === null || v === undefined || v === '' ? null : String(v);
      });
      if (vals.some((v) => v === null)) return '';
      return vals.every((v) => v === vals[0]) ? vals[0] : '';
    };

    return {
      departmentId: agreed((w) => w.Department ?? w.department_id),
      // Profili mund të mbajë disa projekte; i pari është ai që mbush segmentin
      // e parë. Të tjerët shtohen me dorë, sepse ndarja e orëve mes tyre është
      // vendim, jo e dhënë.
      projectId: agreed((w) => (w.ProjectIds ?? w.project_ids ?? [])[0]),
    };
  }, [workers, gridSelectedWorkers]);

  /**
   * Mbushja bëhet vetëm mbi fusha BOSH — një zgjedhje me dorë nuk mbishkruhet
   * kurrë, as kur zgjedhja e punëtorëve ndryshon.
   */
  useEffect(() => {
    const { departmentId, projectId } = profileDefaults;
    if (departmentId) setQuickDepartment((prev) => prev || departmentId);
    if (projectId) {
      setQuickProjectSegs((prev) => (prev.length && !prev[0].project_id
        ? prev.map((seg, i) => (i === 0 ? { ...seg, project_id: projectId } : seg))
        : prev));
    }
  }, [profileDefaults]);

  /* auto-fill editable times when schedule selection changes */
  useEffect(() => {
    if (!quickScheduleId) {
      setQuickStartTime(''); setQuickEndTime(''); setQuickBreakSched('');
      setQuickProjectSegs([]);
      return;
    }
    const sc = allGridSchedules.find(s => String(s.id ?? s.Id) === quickScheduleId);
    if (sc) {
      const st = String(sc.start_time || '').slice(0,5);
      const et = String(sc.end_time || '').slice(0,5);
      setQuickStartTime(st);
      setQuickEndTime(et);
      setQuickBreakSched(String(sc.break_minutes ?? 30));
      // Një segment i vetëm mbi tërë turnin, i lidhur me projektin e PROFILIT
      // të punëtorit. Filtri i shiritit mbetet vetëm rezervë: ai thotë çfarë po
      // shikohet, kurse profili thotë ku bën pjesë njeriu — dhe caktimi ndjek
      // të dytin. Përdoruesi mund ta ndajë më tej.
      // Tipi i shabllonit mbush SEGMENTIN, jo një fushë të dytë: forma e ka një
      // vend të vetëm për llojin e punës, dhe ai është rreshti.
      setQuickProjectSegs(prev => prev.length ? prev
        : [{ id: Date.now(),
             project_id: profileDefaults.projectId || (selProject ? String(selProject) : ''),
             working_type_code: sc.working_type_code || '',
             start: st, end: et }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickScheduleId, allGridSchedules]);

  const toggleGridWorker = useCallback((wid, shiftKey = false, idx = -1, allFiltered = []) => {
    const anchor = lastClickedIdxRef.current;
    setGridSelectedWorkers(prev => selectWorkers(prev, { workerId: wid, shiftKey, anchor, index: idx, visible: allFiltered }));
    if (idx >= 0) lastClickedIdxRef.current = idx;
  }, []);

  /**
   * Cakto orarin për çdo punëtor të zgjedhur.
   *
   * `alloc` vjen nga forma («Rregullimi i orarit») dhe hyn i gatshëm, jo nga
   * gjendja: `setState` është asinkron, ndaj leximi i saj menjëherë pas ruajtjes
   * do të dërgonte vlerat e mëparshme. Orët e turnit dhe orari mbeten nga
   * gjendja — ato nuk i prek forma.
   */
  const handleQuickAssign = async (alloc = null) => {
    const scheduleId = quickScheduleId || defaultScheduleId;
    if (!canEditSchedule || quickAssigning || !scheduleId || gridSelectedWorkers.size === 0) return;
    setQuickAssigning(true); setQuickSnack('');
    let ok = 0, fail = 0;
    const failed = new Set();
    const reasons = new Set();
    const selected = [...gridSelectedWorkers];
    for (const wid of selected) {
      try {
        const r = await fetch(`${API}/api/workSchedules/${scheduleId}/assign`, {
          method:'POST',
          headers: { ...getAuthHeaders(), 'Content-Type':'application/json' },
          body: JSON.stringify({
              worker_id: Number(wid),
              effective_from: quickEffectiveDate || selDate,
              effective_to: quickEffectiveTo || quickEffectiveDate || selDate,
              custom_start_time:    dragWindow?.from || quickStartTime || null,
              custom_end_time:      dragWindow?.to   || quickEndTime   || null,
              custom_break_minutes: quickBreakSched !== '' ? Number(quickBreakSched) : null,
              // Tipi i ditës rrjedh nga segmentet — një kod nëse pajtohen, `null`
              // (pra shablloni) nëse dita është e përzier.
              working_type_code: (() => {
                const codes = new Set(quickProjectSegs.map(s => s.working_type_code).filter(Boolean));
                return codes.size === 1 ? [...codes][0] : null;
              })(),
              long_break_minutes: quickBreakSched !== '' ? Number(quickBreakSched) : null,
              long_break_type_code: quickLongBreakType || null,
              long_break_start: quickLongBreakStart || null,
              short_breaks: quickShortBreaks.map(b => ({ min: Number(b.min)||0, time: b.time||'',
                                                          working_type_code: b.working_type_code || null })),
              description: quickDescription || null,
              department_id: quickDepartment || null,
              project_segments: quickProjectSegs
                .filter(s => s.project_id)
                .map(s => ({ project_id: Number(s.project_id), start_time: s.start || null, end_time: s.end || null,
                             working_type_code: s.working_type_code || null })),
              // Çfarë erdhi nga forma fiton mbi gjendjen — ajo është e fundit
              // që preku njeriu.
              ...(alloc || {}),
            }),
        });
        const j = await r.json();
        if (r.ok && j.success) ok++;
        else { fail++; failed.add(wid); if (j?.error) reasons.add(String(j.error)); }
      } catch (e) { fail++; failed.add(wid); reasons.add(e?.message || 'gabim rrjeti'); }
    }
    setQuickAssigning(false);
    if (fail === 0) {
      loadOverview(monthsToLoad);
      // Pastrimi NUK e prek njoftimin: më parë `setQuickSnack('')` vinte në të
      // njëjtin çast me të, ndaj konfirmimi s'dukej kurrë.
      (() => { setGridSelectedWorkers(new Set()); setQuickScheduleId(''); setQuickEffectiveDate(''); setQuickEffectiveTo(''); setQuickStartTime(''); setQuickEndTime(''); setQuickBreakSched(''); setQuickShortBreaks([]); setQuickLongBreakType(''); setQuickLongBreakStart(''); setDragWindow(null); setQuickDepartment(''); setQuickProjectSegs([]); setQuickDescription(''); lastClickedIdxRef.current = null; })();
      setQuickSnack(`✓ Orari u caktua për ${ok} punëtor${ok !== 1 ? 'ë' : ''}!`);
    } else {
      setGridSelectedWorkers(failed);
      // Arsyeja e serverit (p.sh. «punëtori është me pushim të aprovuar») thuhet,
      // jo vetëm numri: pa të, njeriu s'ka ç'të rregullojë.
      const why = [...reasons].slice(0, 2).join(' · ');
      setQuickSnack(`⚠ ${ok} sukses · ${fail} dështuan${why ? ` — ${why}` : ''} · të dështuarit mbeten të zgjedhur`);
      if (ok > 0) loadOverview(monthsToLoad);
    }
  };

  /**
   * Zgjedhja pastrohet kur ndërron ajo që po shikohet.
   *
   * Data, projekti dhe kërkimi e ndërrojnë listën e rreshtave; punëtorë të
   * zgjedhur që s'duken më do të caktoheshin verbërisht. Prandaj zgjedhja nis
   * nga e para sa herë ndërron pamja.
   *
   * PËRJASHTIM: derisa forma është hapur. Ajo ushqehet pikërisht nga kjo
   * zgjedhje, ndaj pastrimi nën të do t'i hiqte formës punëtorët mbi të cilët
   * po punon. Mbylljen e trajton `onClose`, që pastron vetë.
   *
   * Forma NUK hapet vetë: hapja është veprim i shprehur, me butonin «Cakto
   * orarin». Zgjedhja bëhet me disa klikime — një nga një, ose me Shift mbi një
   * varg — dhe një dritare që del në mes do t'i priste të tjerat.
   */
  useEffect(() => {
    if (allocOpen) return;
    setGridSelectedWorkers(new Set());
    // Bashkë me zgjedhjen ikën edhe dritarja e tërhequr: një tërheqje 09:00–13:00
    // e së hënës nuk ka pse të zbatohet mbi caktimin e së martës.
    setDragWindow(null);
    lastClickedIdxRef.current = null;
  }, [selDate, selProject, search, allocOpen]);


  /* ── Hollësia e rrjetës: sa minuta zë një kolonë ── */
  const [slotMin, setSlotMin] = useState(readStoredSlotMin);
  const slotCount = slotCountFor(slotMin);

  /**
   * «Tani» — minutat nga mesnata, të rifreskuara çdo gjysmë minute.
   *
   * Monitorimi është i gjallë: një sesion i hapur shtrihet deri te ky çast te
   * serveri. Por rrjeta s'kishte asnjë shenjë se ku ndodhet ai çast, ndaj një
   * ditë që sapo ka nisur dukej e tëra «pa ardhur» — dhe kjo lexohej si defekt,
   * jo si e ardhme.
   */
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date(); return d.getHours() * 60 + d.getMinutes();
  });
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes());
    }, 30000);
    return () => clearInterval(id);
  }, []);

  /* visible time range for scrolling/zoom (default: 07:00–19:00) */
  const [visStart, setVisStart] = useState(() => (7 * 60) / readStoredSlotMin());   // 07:00
  const [visEnd,   setVisEnd]   = useState(() => (19 * 60) / readStoredSlotMin());  // 19:00

  /**
   * Ndërrimi i hollësisë ruan dritaren e dukshme, jo numrat e kolonave.
   *
   * `visStart`/`visEnd` numërojnë KOLONA, dhe kolona sapo ndryshoi gjatësi: po
   * t'i linim ashtu, kalimi nga 10 në 30 minuta do ta hidhte pamjen 07:00–19:00
   * te 21:00–24:00 e më tej jashtë ditës. Prandaj përkthehen përmes minutave —
   * ora që po shikohej mbetet ora që shihet.
   */
  const changeSlotMin = (next) => {
    if (!SLOT_MIN_OPTIONS.includes(next) || next === slotMin) return;
    const startMin = visStart * slotMin;
    const endMin   = Math.min(SLOT_END, visEnd * slotMin);
    const count    = slotCountFor(next);
    setSlotMin(next);
    setVisStart(Math.max(0, Math.floor(startMin / next)));
    setVisEnd(Math.min(count, Math.max(Math.floor(startMin / next) + 1, Math.ceil(endMin / next))));
    try { window.localStorage.setItem(SLOT_MIN_KEY, String(next)); } catch { /* noop */ }
  };

  /**
   * Teksti i fushës rri veçmas nga vlera e rrjetës.
   *
   * Fusha shkruhet me dorë, dhe gjatë shkrimit kalon nëpër gjendje që s'janë
   * vlera: «1» në rrugë e sipër për te «15», ose bosh kur fshihet për t'u
   * rishkruar. Po të vizatonte rrjeta çdo shkronjë, ajo do të rindërtohej dy
   * herë për një numër dyshifror.
   */
  const [slotMinText, setSlotMinText] = useState(() => String(readStoredSlotMin()));
  const commitSlotMin = () => {
    const next = snapSlotMin(slotMinText);
    setSlotMinText(String(next));
    changeSlotMin(next);
  };

  /* ── Derive selected date info ── */
  const selDateObj = useMemo(() => parseLocalDate(selDate), [selDate]);
  const selDayStr  = WEEKDAYS_SQ_SHORT[selDateObj.getDay()];
  const selDayNum  = selDateObj.getDate();
  const selMonthStr = MONTHS_SQ[selDateObj.getMonth()];
  const selYearStr  = selDateObj.getFullYear();
  const isToday    = selDate === TODAY;

  /* ── Load workers + projects once ── */
  useEffect(() => {
    const headers = getAuthHeaders();
    let cancelled = false;
    setWorkersLoading(true);
    setWorkersError(null);

    // selectworker only returns active workers; fall back to /api/workers (all) so
    // the user can see what's going on if every worker has been terminated.
    const fetchWorkers = async () => {
      let resp;
      try {
        resp = await fetch(`${API}/api/query/selectworker`, { headers });
      } catch (e) {
        return { error: { type: 'network', detail: e.message }, data: [] };
      }
      if (resp.status === 401 || resp.status === 403) {
        return { error: { type: 'auth', detail: `HTTP ${resp.status}` }, data: [] };
      }
      if (!resp.ok) {
        return { error: { type: 'http', detail: `HTTP ${resp.status}` }, data: [] };
      }
      const j = await resp.json().catch(() => ({}));
      const activeList = Array.isArray(j?.data) ? j.data : [];
      if (activeList.length > 0) return { data: activeList };

      // selectworker came back empty — check whether ANY workers exist (active=false)
      // so we can give the user a precise diagnosis.
      try {
        const allResp = await fetch(`${API}/api/workers`, { headers });
        if (allResp.ok) {
          const allJson = await allResp.json().catch(() => ({}));
          const allList = Array.isArray(allJson?.data) ? allJson.data : [];
          if (allList.length > 0) {
            return { error: { type: 'all_inactive', detail: `${allList.length} workers exist but none are active` }, data: [] };
          }
          return { error: { type: 'empty_db', detail: 'no workers in database' }, data: [] };
        }
      } catch (_) { /* noop — fall through to generic empty */ }
      return { error: { type: 'empty', detail: 'selectworker returned 0 rows' }, data: [] };
    };

    Promise.all([
      fetchWorkers(),
      fetch(`${API}/api/projects`, { headers }).then(r=>r.json()).catch(()=>({data:[]})),
      fetch(`${API}/api/departments`, { headers }).then(r=>r.json()).catch(()=>({data:[]})),
    ]).then(([wr, pr, dr]) => {
      if (cancelled) return;
      const projList = pr.data || [];
      setWorkers(wr.data || []);
      setProjects(projList);
      setDepartments(dr.data || []);
      // Default: preselect the first project instead of "Të gjithë projektet"
      // Vetëm kur njeriu s'ka prekur ende filtrin: ndryshe «Të gjithë projektet»
      // (vlera bosh) zëvendësohej nga projekti i parë sapo mbërrinte lista.
      if (!projectTouchedRef.current) {
        setSelProject(prev => prev || (projList[0] ? String(projList[0].id || projList[0].Id) : ''));
      }
      setWorkersError(wr.error || null);
      setWorkersLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  /* ── Load daily overview ── */
  /**
   * Muajt që duhen ngarkuar për periudhën e zgjedhur.
   *
   * API-ja është e ndarë sipas muajve (`year`+`month`), ndaj «3 muaj» janë tri
   * kërkesa dhe një bashkim — jo një rrugë e re. Një javë që kalon fundin e
   * muajit ka nevojë për dy.
   */
  const monthsToLoad = useMemo(() => {
    const { from, to } = periodRange(selDate, period);
    const out = []; const d = new Date(from); d.setDate(1);
    const end = new Date(to);
    while (d <= end) { out.push({ y: d.getFullYear(), m: d.getMonth() + 1 }); d.setMonth(d.getMonth() + 1); }
    return out;
  }, [selDate, period]);
  const monthsKey = monthsToLoad.map(x => `${x.y}-${x.m}`).join(',');

  // Numri i kërkesës: një përgjigje e vjetër që mbërrin pas një të reje NUK e
  // mbishkruan ekranin. Ndryshimi i shpejtë projekt→periudhë i kthente më parë
  // të dhënat e zgjedhjes së mëparshme.
  const overviewReqRef = useRef(0);
  const loadOverview = useCallback(async (months) => {
    const reqId = ++overviewReqRef.current;
    const fresh = () => reqId === overviewReqRef.current;
    setLoading(true);
    try {
      const pid  = selProject ? `&project_id=${selProject}` : '';
      // Monitorimi kërkohet vetëm për ditën që po shihet: rrjeta ngjyros një
      // ditë në një çast, dhe një muaj i plotë segmentesh do të fryente
      // përgjigjen pa u parë kurrë. Toleranca e shiritit udhëton bashkë me të
      // — ndarja bëhet në server, që rrjeta dhe raportet të pajtohen.
      const parts = await Promise.all(months.map(async ({ y, m }) => {
        const resp = await fetch(
          `${API}/api/reports/work-schedule-overview?year=${y}&month=${m}${pid}`
          + `&monitor_date=${selDate}&tolerance=${tolApplied}`,
          { headers: getAuthHeaders() },
        );
        const json = await resp.json();
        return json?.success ? json : null;
      }));
      const ok = parts.filter(Boolean);
      if (!fresh()) return;
      if (!ok.length) { setOverview(null); return; }
      if (ok.length === 1) { setOverview(ok[0]); return; }

      // Bashkimi është për punëtor: `days` mblidhen, kurse fushat përfaqësuese
      // (orari, emri) merren nga muaji i parë që i ka — ato s'ndryshojnë brenda
      // periudhës, dhe një «i fundit fiton» do t'i zhvendoste pa arsye.
      const byWorker = new Map();
      for (const part of ok) {
        for (const w of part.workers || []) {
          const prev = byWorker.get(w.worker_id);
          if (!prev) byWorker.set(w.worker_id, { ...w, days: { ...(w.days || {}) } });
          else Object.assign(prev.days, w.days || {});
        }
      }
      const weeks = [];
      const seenWeek = new Set();
      for (const part of ok) for (const wk of part.weeks || []) {
        if (!seenWeek.has(wk.iso_week)) { seenWeek.add(wk.iso_week); weeks.push(wk); }
      }
      setOverview({
        success: true,
        days:    ok.flatMap(p => p.days || []),
        weeks,
        workers: [...byWorker.values()],
      });
    } catch (e) {
      console.error('[OretPage overview]', e.message);
      if (fresh()) setOverview(null);
    } finally {
      // `finally` sepse më parë një dalje e parakohshme (asnjë muaj i vlefshëm)
      // e linte rrotullën e ngarkimit përgjithmonë në ekran.
      if (fresh()) setLoading(false);
    }
  }, [selProject, selDate, tolApplied]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadOverview(monthsToLoad); }, [monthsKey, loadOverview]);

  /* update year/month when selDate changes */
  useEffect(() => {
    const d = parseLocalDate(selDate);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }, [selDate]);

  /* Klikimi mbi emrin hap «Orarin Individual Mujor» — pamja vetëm-shikim, jo një
   * kopje e dytë e rrjetës. Rreshti vetë mbetet për përzgjedhjen e shumëfishtë,
   * ndaj emri e ndal ngjarjen: dy kuptime për të njëjtin klikim do të thoshin që
   * njëri prej tyre humbet. */
  const history = useHistory();
  const openIndividualSchedule = useCallback((wid) => {
    // Data udhëton bashkë me punëtorin: raporti është mujor, dhe pa të do të
    // hapej te muaji i sotëm edhe kur po shikohet korriku.
    if (wid) history.push(`/home/reports/work-schedule-matrix?worker=${wid}&date=${selDate}`);
  }, [history, selDate]);

  /* ── Navigate dates ── */
  const prevDay = () => {
    const d = parseLocalDate(selDate); d.setDate(d.getDate() - 1); setSelDate(localStr(d));
  };
  const nextDay = () => {
    const d = parseLocalDate(selDate); d.setDate(d.getDate() + 1); setSelDate(localStr(d));
  };

  /* ── Build worker daily data from overview (for selected date) ── */
  const workerDayMap = useMemo(() => {
    if (!overview?.workers) return {};
    const m = {};
    for (const w of overview.workers) {
      const d = w.days?.[selDate];
      if (d) {
        m[w.worker_id] = {
          ...d,
          // Orari i KËSAJ dite fiton mbi atë përfaqësues të punëtorit: orari
          // mund të ketë ndryshuar brenda muajit, dhe monitorimi i ditës duhet
          // të matet kundrejt planit që vlente atë ditë.
          schedule_start: d.schedule_start || w.schedule_start,
          schedule_end:   d.schedule_end   || w.schedule_end,
          net_worked_minutes: d.net_worked_minutes,
          break_minutes: d.break_minutes || 0,
          overtime_minutes: d.overtime_minutes || 0,
          late_minutes: d.late_minutes || 0,
          check_in: d.check_in,
          check_out: d.check_out,
        };
      }
    }
    return m;
  }, [overview, selDate]);

  /* ── Auto-default the quick schedule ──────────────────────────────────────────
   * When workers get selected, pre-fill "Zgjidh orarin" with the schedule the
   * (first) selected worker already has from their project/contract — so the user
   * doesn't pick it manually. Falls back to the first schedule when there's no
   * match. Only fills when empty, so a manual choice / clear is respected. */
  useEffect(() => {
    if (!allGridSchedules.length || gridSelectedWorkers.size === 0) return;
    if (quickScheduleId) return;
    const firstId = [...gridSelectedWorkers][0];
    const wd = workerDayMap[firstId] || workerDayMap[String(firstId)];
    const wStart = String(wd?.schedule_start || '').slice(0, 5);
    const wEnd   = String(wd?.schedule_end   || '').slice(0, 5);
    const match = (wStart && wEnd)
      ? allGridSchedules.find(s => String(s.start_time || '').slice(0, 5) === wStart
                                 && String(s.end_time   || '').slice(0, 5) === wEnd)
      : null;
    const def = match || allGridSchedules.find(s => s.is_default) || allGridSchedules[0];
    if (def) setQuickScheduleId(String(def.id ?? def.Id));
  }, [gridSelectedWorkers, allGridSchedules, workerDayMap]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Filtered workers ── */
  const filteredWorkers = useMemo(() => {
    let list = workers;
    // When a project is selected, show ONLY workers assigned to it. The overview
    // is fetched with project_id, so overview.workers already contains exactly
    // the workers in that project — restrict the row list to that set.
    if (selProject && overview?.workers) {
      const ids = new Set(overview.workers.map(w => String(w.worker_id)));
      list = list.filter(w => ids.has(String(w.Id ?? w.id)));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(w =>
        `${w.FirstName||w.first_name||''} ${w.LastName||w.last_name||''}`.toLowerCase().includes(q) ||
        (w.EmployeeCode || w.employee_code || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [workers, search, selProject, overview]);

  /**
   * ── Shifrat e shiritit ────────────────────────────────────────────────────
   *
   * Dy gabime rrinin këtu bashkë, dhe secili e bënte tjetrin të padukshëm.
   *
   * **1. Pauza zbritej dy herë.** `net_worked_minutes` vjen NETO nga serveri
   * (server.js: `net = breakPaid ? work : work − break`), kurse këtu zbritej
   * sërish për «Produktive». Me pauzë të papaguar numri dilte `punë − pauzë −
   * pauzë`; me pauzë të paguar zbritej një pauzë që s'duhej zbritur fare.
   *
   * **2. Shiriti lexonte sesione të MBYLLURA, rrjeta poshtë tij të gjallat.**
   * Një ditë në ecuri i tregonte të gjitha zero ndërsa rrjeta pikturonte «në
   * punë» — dy të vërteta mbi një ekran. Tani shiriti llogaritet nga vetë
   * intervalet e hyrje/daljeve (`presenceMinutes`): sesioni i hapur i SOTËM
   * shtrihet deri tani, ai i një dite të mbyllur nuk mbulon asgjë, dhe puna
   * jashtë orarit nuk pritet më te kufijtë e planit.
   *
   * **«Produktive» pushoi së qeni zbritje dhe u bë ndarje.** Dikur ishte
   * «prezencë − pauzë», pra s'e dallonte dot një orë pune nga një orë mbledhjeje.
   * Tani mbledhjet, boshllëqet dhe koha pa aktivitet dalin prej saj — ato
   * mbeten brenda «Punuar», sepse njeriu ishte këtu dhe paguhet.
   */
  /* ── Intervali që po shikohet ── */
  const range      = useMemo(() => periodRange(selDate, period), [selDate, period]);
  const rangeDates = useMemo(() => datesBetween(range.from, range.to), [range]);
  // Njësia e kolonës ndjek gjatësinë: ditë për javë e muaj, javë për tre muaj.
  const rangeColumns = useMemo(() => periodColumns(rangeDates, period), [rangeDates, period]);

  const stats = useMemo(() => {
    if (!overview?.workers) return null;
    // Pilulat ndjekin RRESHTAT në ekran: kërkimi te kutia e punëtorit ndryshonte
    // rreshtat dhe Excel-in, por jo numrat mbi ta — dy të vërteta në një pamje.
    const shownIds = new Set(filteredWorkers.map((w) => String(w.Id ?? w.id)));
    const acc = { total: 0, productive: 0, paused: 0, absent: 0, late: 0, overtime: 0,
                  total_w: 0, live: false, planned: 0, plannedBreak: 0, workDays: 0, leaves: new Map(),
                  dayOff: 0, dayOffDays: 0 };
    // Një pilulë e ditës mbi një pamje javore do të ishte gënjeshtër e heshtur:
    // etiketa nuk ndryshon, numri po. Prandaj mbledhja ndjek periudhën.
    for (const w of overview.workers) {
      if (shownIds.size && !shownIds.has(String(w.worker_id ?? w.id))) continue;
      if (rangeDates.some((dt) => w.days?.[dt])) acc.total_w++;
      const t = sumWorkerDays(w.days, rangeDates);
      acc.total        += t.worked;
      acc.productive   += t.productive;
      acc.paused       += t.paused;
      acc.planned      += t.planned;
      acc.plannedBreak += t.plannedBreak;
      acc.overtime     += t.overtime;
      acc.late         += t.late;
      acc.absent       += t.absentDays;
      acc.dayOff       += t.dayOff;
      acc.dayOffDays   += t.dayOffDays;
      acc.workDays     += t.workDays;
      for (const [k, v] of t.leaves) {
        const cur = acc.leaves.get(k) || { ...v, days: 0 };
        cur.days += v.days; acc.leaves.set(k, cur);
      }
      if (t.live) acc.live = true;
    }
    return acc;
  }, [overview, rangeDates, filteredWorkers]);

  /** Llojet e aktivitetit që janë vërtet në ekran atë ditë — jo katalogu. */
  const activityLegend = useMemo(() => {
    const seen = new Map();
    for (const w of overview?.workers || []) {
      for (const a of w.days?.[selDate]?.activities || []) {
        if (!seen.has(a.type)) {
          const p = ACTIVITY[a.type] || ACTIVITY.break;
          seen.set(a.type, { ...p, label: t(p.key), hint: t('oret.fromWorkClock', { name: t(p.key) }) });
        }
      }
    }
    return [...seen.values()];
  }, [overview, selDate]);

  /** Llojet e pushimit të pranishme te dita e zgjedhur, pa dublikatë. */
  const leaveLegend = useMemo(() => {
    const seen = new Map();
    for (const w of overview?.workers || []) {
      const lv = w.days?.[selDate]?.leave;
      if (lv && !seen.has(lv.TypeId)) seen.set(lv.TypeId, lv);
    }
    return [...seen.values()];
  }, [overview, selDate]);

  /* ── Build slots per worker for grid ── */
  const slotsMap = useMemo(() => {
    const m = {};
    for (const w of filteredWorkers) {
      const wid = w.Id ?? w.id;
      const dayData = workerDayMap[wid];
      m[wid] = buildSlots(dayData, slotMin);
    }
    return m;
  }, [filteredWorkers, workerDayMap, slotMin]);

  /**
   * Eksporti merr ATË që sheh: punëtorët e filtruar, periudha e zgjedhur, dhe
   * i njëjti `sumWorkerDays` që ushqen shiritin. Pa këtë të fundit, skedari që
   * shkon te klienti do të mospajtohej me ekranin ku u pa.
   */
  const onExportExcel = () => {
    const projectName = projects.find((p) => String(p.id ?? p.Id) === String(selProject));
    exportOretExcel(filteredWorkers, {
      dayMap: overviewWorkerMap,
      dates: rangeDates,
      sum: sumWorkerDays,
      period: { label: t(PERIODS.find((p) => p.key === period)?.labelKey || ''), from: range.from, to: range.to },
      projectName: projectName ? (projectName.name || projectName.Name) : '',
      today: TODAY,
      labels: {
        title: t('oret.expTitle'), worker: t('oret.worker'), code: t('oret.expCode'),
        shift: t('oret.expShift'), worked: t('oret.worked'), planned: t('oret.plannedHours'),
        diff: t('oret.difference'), productive: t('oret.productive'), pause: t('oret.breakLabel'),
        overtime: t('oret.overtime'), late: t('oret.expLateMin'),
        absentDays: t('oret.expAbsDays'), workDays: t('oret.expWorkDays'),
        breakTimes: t('oret.expBreakTimes'),
        period: t('oret.expPeriod'), project: t('oret.project'),
        allProjects: t('oret.allProjects'), workersTotal: t('oret.expWorkers'),
        generated: t('oret.expIssued'), total: t('oret.expTotal'),
      },
    });
  };

  const selectedWorkerIds = useMemo(
    () => [...gridSelectedWorkers].map(Number), [gridSelectedWorkers]);

  const visibleRange = { start: visStart, end: visEnd };

  /**
   * Kolona ku bie «tani» — vetëm kur po shikohet dita e sotme; përndryshe null,
   * se një vijë «tani» mbi 3 shtatorin nuk do të thoshte asgjë.
   */
  // `isToday` është përcaktuar më lart (rreshti ~1346) — s'ka nevojë për një të dytë.
  const nowSlot = isToday ? Math.floor((nowMin - SLOT_START) / slotMin) : null;

  /**
   * Dritarja e parazgjedhur 07:00–19:00 e fsheh mëngjesin e hershëm. Me një turn
   * që nis 05:51 dhe orën 06:25, gjithçka që kishte ndodhur rrinte majtas skajit
   * dhe ekrani dukej sikur s'kishte ardhur askush.
   *
   * Prandaj, kur hapet dita e sotme, dritarja ZHVENDOSET (jo zgjerohet) aq sa
   * «tani» të bjerë brenda saj — gjerësia mbetet ajo që zgjodhi njeriu. Vetëm te
   * ndërrimi i datës: pas kësaj, zoom-i dhe rrëshqitja janë të tijat.
   */
  const shiftedForRef = useRef(null);
  useEffect(() => {
    if (!isToday || nowSlot === null) return;
    if (shiftedForRef.current === selDate) return;
    shiftedForRef.current = selDate;
    setVisStart((st) => {
      const width = visEnd - st;
      if (nowSlot >= st && nowSlot < visEnd) return st;
      // Një orë hapësirë para «tani», që të mos ngjitet pas skajit.
      const next = Math.max(0, Math.min(nowSlot - Math.round(60 / slotMin), slotCount - width));
      setVisEnd(next + width);
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selDate, isToday, nowSlot]);

  /* ── Visible time label ── */
  // Ora e vërtetë e skajeve, me minuta. Më parë këtu rrinte `:00` i shkruar me
  // dorë mbi një orë të rrumbullakosur poshtë: një pamje që fillonte 04:40
  // lexohej «04:00–21:00», pra tregonte dyzet minuta që s'ishin në ekran.
  const edgeLabel = (col) => {
    const min = Math.min(SLOT_END, col * slotMin);
    return `${String(Math.floor(min / 60)).padStart(2,'0')}:${String(min % 60).padStart(2,'0')}`;
  };
  const visStartLabel = edgeLabel(visStart);
  const visEndLabel   = edgeLabel(visEnd);

  /* ── Zoom controls ── */
  const zoomIn  = () => {
    const mid = Math.floor((visStart + visEnd) / 2);
    const half = Math.max(4, Math.floor((visEnd - visStart) / 2 * 0.7));
    setVisStart(Math.max(0, mid - half));
    setVisEnd(Math.min(slotCount, mid + half));
  };
  const zoomOut = () => {
    const mid = Math.floor((visStart + visEnd) / 2);
    const half = Math.min(slotCount/2, Math.ceil((visEnd - visStart) / 2 * 1.4));
    setVisStart(Math.max(0, mid - half));
    setVisEnd(Math.min(slotCount, mid + half));
  };
  const showFull = () => { setVisStart(0); setVisEnd(slotCount); };

  /* ── Worker → project name map ── */
  const workerProjectMap = useMemo(() => {
    const m = {};
    if (!overview?.workers) return m;
    // If a project is selected, all returned workers belong to that project
    if (selProject) {
      const proj = projects.find(p => String(p.id || p.Id) === String(selProject));
      const projName = proj ? (proj.name || proj.Name || '') : '';
      for (const w of overview.workers) {
        if (projName) m[w.worker_id] = projName;
      }
    }
    // Also use per-worker project_names from overview
    for (const w of overview.workers) {
      if (!m[w.worker_id] && w.project_names) {
        m[w.worker_id] = w.project_names;
      }
    }
    return m;
  }, [overview, selProject, projects]);

  /* ── Overview worker map (for weekly totals / cumulative) ── */
  const overviewWorkerMap = useMemo(() => {
    if (!overview?.workers) return {};
    const m = {};
    for (const w of overview.workers) m[w.worker_id] = w;
    return m;
  }, [overview]);

  /* ── Weeks from overview ── */
  const overviewWeeks = overview?.weeks || [];

  /* ── Slot count summary (how many workers active in each slot) ── */
  const slotCounts = useMemo(() => {
    const counts = new Array(slotCount).fill(0);
    for (const w of filteredWorkers) {
      const wid = w.Id ?? w.id;
      const slots = slotsMap[wid];
      if (!slots) continue;
      for (let s = 0; s < slotCount; s++) {
        if (slots[s] && slots[s] !== 'empty' && slots[s] !== 'off') counts[s]++;
      }
    }
    return counts;
  }, [filteredWorkers, slotsMap, slotCount]);

  /* ── Grid width ── */
  const extraW = (showTeamleader ? TL_COL_W : 0) + (showProject ? PROJ_COL_W : 0) + (showDepartment ? DEPT_COL_W : 0);
  const weekColsW = overviewWeeks.length * RIGHT_COL_W;
  const grandColsW = 5 * (RIGHT_COL_W + 8);
  const gridW = (visEnd - visStart) * CELL_W + NAME_W + extraW + RIGHT_COL_DEFS.length * RIGHT_COL_W + weekColsW + grandColsW;

  /* ─────────────────────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden', bgcolor:G.zebra }}>

      {/* ── TOP BAR ──────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        px:'12px', py:'6px', borderBottom:`1px solid ${G.lineSoft}`,
        display:'flex', flexDirection:'column', gap:'5px',
        bgcolor:'#fff', zIndex:10, flexShrink:0,
      }}>
        {/* Row 1: all controls in one line */}
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">

          {/* Date navigator */}
          <Box sx={{
            display:'flex', alignItems:'center', gap:.25,
            border:`1px solid ${G.edge}`, borderRadius:'7px', px:.5, py:.25, bgcolor:G.zebra,
            flexShrink:0,
          }}>
            <IconButton size="small" onClick={prevDay} sx={{ p:.3, '&:hover':{ bgcolor:G.headAlt } }}>
              <ChevronLeftIcon sx={{ fontSize:16 }} />
            </IconButton>
            {/* `TODAY` është data LOKALE: me UTC, mes mesnate dhe orës 02:00
                klikimi mbi datën të çonte te dita e djeshme. */}
            <Box sx={{ textAlign:'center', minWidth:110, px:.25, cursor:'pointer' }} onClick={() => setSelDate(TODAY)}>
              <Typography sx={{ fontSize:11.5, fontWeight:700, color: isToday ? '#1565c0' : G.ink, lineHeight:1.2, whiteSpace:'nowrap' }}>
                {selDayStr}, {selDayNum} {selMonthStr} {selYearStr}
              </Typography>
              <Typography sx={{ fontSize:9, color: isToday ? '#42a5f5' : G.inkFaint, lineHeight:1, fontWeight: isToday ? 600 : 400 }}>
                {isToday ? `● ${t('oret.today')}` : selDate}
              </Typography>
            </Box>
            <IconButton size="small" onClick={nextDay} sx={{ p:.3, '&:hover':{ bgcolor:G.headAlt } }}>
              <ChevronRightIcon sx={{ fontSize:16 }} />
            </IconButton>
            <TextField
              type="date" size="small"
              value={selDate}
              onChange={e => setSelDate(e.target.value)}
              inputProps={{ style:{ padding:'2px 4px', fontSize:10.5, width:96 } }}
              sx={{ '& .MuiOutlinedInput-notchedOutline':{ border:'none' }, ml:.25 }}
            />
          </Box>

          {/* Periudha. Grup butonash, jo listë: janë katër, dhe një listë do të
              kërkonte dy klikime për një ndërrim që bëhet vazhdimisht. Data
              mbetet po ajo — ajo thotë KU je brenda periudhës, jo sa gjatë. */}
          <Box sx={{ display:'flex', borderRadius:'7px', overflow:'hidden',
            border:`1px solid ${G.edge}`, flexShrink:0 }}>
            {PERIODS.map((p, i) => {
              const on = period === p.key;
              return (
                <Box key={p.key} component="button" onClick={() => changePeriod(p.key)}
                  title={p.key === 'day'
                    ? t('oret.periodDayHint')
                    : `${t('oret.periodRangeHint')} · ${periodRange(selDate, p.key).from} – ${periodRange(selDate, p.key).to}`}
                  sx={{ fontSize:10.5, px:'10px', height:26, cursor:'pointer', fontWeight: on ? 800 : 600,
                    bgcolor: on ? '#E3F0F4' : G.zebra, color: on ? '#1a6b7d' : G.inkStrong,
                    border:'none', borderLeft: i > 0 ? `1px solid ${G.edge}` : 'none',
                    transition:'all .12s', lineHeight:1,
                    '&:hover':{ bgcolor: on ? '#E3F0F4' : G.headAlt } }}>
                  {t(p.labelKey)}
                </Box>
              );
            })}
          </Box>

          {/* Divider */}
          <Box sx={{ width:'1px', height:24, bgcolor:G.lineSoft, flexShrink:0 }} />

          {/* Project selector */}
          <FormControl size="small" sx={{ minWidth:150, flexShrink:0 }}>
            <Select
              value={selProject}
              onChange={e => { projectTouchedRef.current = true; setSelProject(e.target.value); }}
              displayEmpty
              sx={{ fontSize:11, height:28, borderRadius:'7px',
                '& .MuiSelect-select':{ py:'3px', px:'10px' },
                '& .MuiOutlinedInput-notchedOutline':{ borderColor:G.hourBg },
                '&:hover .MuiOutlinedInput-notchedOutline':{ borderColor:G.inkFaint },
              }}
            >
              <MenuItem value=""><em style={{fontSize:11}}>{t('oret.allProjects')}</em></MenuItem>
              {projects.map(p => (
                <MenuItem key={p.id||p.Id} value={p.id||p.Id} sx={{ fontSize:11 }}>
                  {p.name||p.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Divider */}
          <Box sx={{ width:'1px', height:24, bgcolor:G.lineSoft, flexShrink:0 }} />

          {/* View toggles — icon-style toggle chips */}
          {[
            // Etiketat dhe shpjegimet në një gjuhë. «TL» ishte shkurtesë që s'e
            // shpjegonte askush, dhe tri nga katër shpjegimet ishin anglisht mbi
            // një ekran shqip.
            { key:'tl',  label:t('oret.supervisor'),       checked: showTeamleader, set: setShowTeamleader, title:t('oret.supervisor') },
            { key:'pr',  label:t('oret.project'),     checked: showProject,    set: setShowProject,    title:t('oret.project') },
            { key:'dep', label:t('oret.department'), checked: showDepartment, set: setShowDepartment, title:t('oret.department') },
            { key:'mon', label:'Monitoring',   checked: showMonitoring, set: setShowMonitoring,
              title:t('oret.hintMonitoring') },
          ].map(t => (
            <Box key={t.key}
              onClick={() => t.set(p => !p)}
              title={t.title}
              sx={{
                display:'flex', alignItems:'center', gap:.35, cursor:'pointer', flexShrink:0,
                borderRadius:'6px', px:.8, height:26,
                border:'1px solid', transition:'all .13s',
                borderColor: t.checked ? '#90caf9' : G.hourBg,
                bgcolor: t.checked ? '#e3f2fd' : G.zebra,
                '&:hover':{ borderColor: t.checked ? '#64b5f6' : G.lineMid, bgcolor: t.checked ? '#bbdefb' : G.hover },
              }}>
              <Box sx={{
                width:12, height:12, borderRadius:'3px', border:'1.5px solid',
                borderColor: t.checked ? '#1565c0' : G.inkFaint,
                bgcolor: t.checked ? '#1565c0' : 'transparent',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
              }}>
                {t.checked && <Typography sx={{ color:'#fff', fontSize:8, lineHeight:1, fontWeight:900 }}>✓</Typography>}
              </Box>
              <Typography sx={{ fontSize:10.5, fontWeight: t.checked ? 600 : 400, color: t.checked ? '#1565c0' : G.inkMid, lineHeight:1 }}>
                {t.label}
              </Typography>
            </Box>
          ))}

          {/* Legjenda e monitorimit — ngjyra nuk guxon të jetë i vetmi informacion.
              Çdo gjendje ka një shenjë që përsëritet brenda qelizave, dhe çdo
              qelizë mban intervalin e saktë te `title`. */}
          {showMonitoring && (
            <>
              <Box sx={{ width:'1px', height:24, bgcolor:G.lineSoft, flexShrink:0 }} />
              <Box sx={{ display:'flex', alignItems:'center', gap:.75, flexShrink:0 }}>
                {[
                  { ...MONITOR_COLORS.covered, label:t('oret.atWork'),   hint:t('oret.hintAtWork') },
                  { ...MONITOR_COLORS.missing, label:t('oret.missing'),   hint:t('oret.hintMissing') },
                  { ...MONITOR_TOLERATED,      label:t('oret.toleranceState'), hint:t('oret.hintTolerance') },
                  // «Pa ardhur» u hoq: ajo gjendje s'pikturohet më. E ardhmja e
                  // planit mban ngjyrën e llojit të vet, dhe një legjendë për një
                  // ngjyrë që s'del kurrë mëson të shpërfillet.
                  { bg: PLAN_BAND_BG, fg: PLAN_BAND_BORDER, mark: '',
                    label:t('oret.planState'), hint:t('oret.hintPlan') },
                ].concat(activityLegend).map(c => (
                  <Box key={c.label} title={c.hint} sx={{ display:'flex', alignItems:'center', gap:.3 }}>
                    <Box sx={{
                      width:13, height:13, borderRadius:'3px', flexShrink:0,
                      bgcolor:c.bg, color:c.fg, border:`1px solid ${G.inkStrong}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:8, fontWeight:900, lineHeight:1,
                    }}>{isBreakToken(c.mark) ? <BreakIcon token={c.mark} /> : c.mark}</Box>
                    <Typography sx={{ fontSize:10, color:G.inkMid, whiteSpace:'nowrap' }}>{c.label}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Divider */}
          <Box sx={{ width:'1px', height:24, bgcolor:G.lineSoft, flexShrink:0 }} />

          {/* Tolerance */}
          <Box sx={{ display:'flex', alignItems:'center', gap:.4, flexShrink:0 }}>
            <Typography sx={{ fontSize:10.5, color:G.inkSoft, fontWeight:500, whiteSpace:'nowrap' }}>Tol.</Typography>
            <TextField
              type="number" size="small" value={tolerance}
              onChange={e => setTolerance(Number(e.target.value))}
              inputProps={{ min:0, max:60, style:{ padding:'2px 4px', fontSize:11, width:36, textAlign:'center' } }}
              sx={{ '& .MuiOutlinedInput-root':{ height:26, borderRadius:'6px',
                '& .MuiOutlinedInput-notchedOutline':{ borderColor:G.hourBg } } }}
            />
            <Typography sx={{ fontSize:10, color:G.inkFaint }}>min</Typography>
          </Box>

          {/* Hollësia e rrjetës — sa minuta zë një kolonë.
              Rri pranë tolerancës sepse të dyja maten në minuta dhe lexohen
              bashkë: një tolerancë 15-minutëshe nuk shihet dot mbi një kolonë
              që zë 30. */}
          <Box sx={{ display:'flex', alignItems:'center', gap:.4, flexShrink:0 }}>
            <Typography sx={{ fontSize:10.5, color:G.inkSoft, fontWeight:500, whiteSpace:'nowrap' }}>Kolona</Typography>
            <TextField
              type="number" size="small" value={slotMinText}
              onChange={e => setSlotMinText(e.target.value)}
              onBlur={commitSlotMin}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              title={`Sa minuta zë një kolonë e rrjetës. Ruhet për herën tjetër. Vlerat që e ndajnë orën: ${SLOT_MIN_OPTIONS.join(', ')}`}
              inputProps={{ min:SLOT_MIN_OPTIONS[0], max:SLOT_MIN_OPTIONS[SLOT_MIN_OPTIONS.length-1],
                style:{ padding:'2px 4px', fontSize:11, width:36, textAlign:'center' } }}
              sx={{ '& .MuiOutlinedInput-root':{ height:26, borderRadius:'6px',
                '& .MuiOutlinedInput-notchedOutline':{ borderColor:G.hourBg } } }}
            />
            <Typography sx={{ fontSize:10, color:G.inkFaint }}>min</Typography>
          </Box>

          {/* Search */}
          <TextField
            size="small" placeholder={t('oret.searchWorker')}
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize:13, color:G.inkFaint, mr:.4 }} /> }}
            inputProps={{ style:{ padding:'2px 6px', fontSize:11, width:120 } }}
            sx={{ '& .MuiOutlinedInput-root':{ height:26, borderRadius:'7px',
              '& .MuiOutlinedInput-notchedOutline':{ borderColor:G.hourBg },
              '&:hover .MuiOutlinedInput-notchedOutline':{ borderColor:G.inkFaint },
            }, flexShrink:0 }}
          />

          {/* Eksporti. Rri pranë periudhës sepse ATË eksporton: ç'sheh, merr. */}
          <Box component="button" onClick={onExportExcel}
            title={t('oret.exportHint')}
            sx={{ display:'flex', alignItems:'center', gap:.4, flexShrink:0,
              fontSize:10.5, px:'9px', height:26, cursor:'pointer', fontWeight:700,
              bgcolor:G.zebra, color:G.inkStrong, border:`1px solid ${G.edge}`,
              borderRadius:'7px', lineHeight:1, transition:'all .12s',
              '&:hover':{ bgcolor:G.headAlt, color:G.ink } }}>
            <FileDownloadRoundedIcon sx={{ fontSize:14 }} />
            {t('oret.exportExcel')}
          </Box>

          {/* Zoom group */}
          <Box sx={{ display:'flex', borderRadius:'7px', overflow:'hidden', border:`1px solid ${G.edge}`, flexShrink:0 }}>
            {[
              { label: '+', onClick: zoomIn,  title:'Zoom in' },
              { label: '−', onClick: zoomOut, title:'Zoom out' },
              { label: '24h', onClick: showFull, title:'Pamja 24h' },
              { label: '05–22', onClick: () => { setVisStart((5 * 60) / slotMin); setVisEnd((22 * 60) / slotMin); }, title:'05:00–22:00' },
            ].map((btn, i) => (
              <Box key={btn.label} component="button" onClick={btn.onClick} title={btn.title}
                sx={{
                  fontSize:10.5, px:'9px', height:26, cursor:'pointer', fontWeight:600,
                  bgcolor:G.zebra, color:G.inkStrong,
                  border:'none', borderLeft: i > 0 ? `1px solid ${G.edge}` : 'none',
                  transition:'all .12s', lineHeight:1,
                  '&:hover':{ bgcolor:G.headAlt, color:G.ink },
                }}>
                {btn.label}
              </Box>
            ))}
          </Box>
        </Stack>

        {/* Row 2: summary stats */}
        {stats && (
          <Stack direction="row" gap={.5} flexWrap="wrap" alignItems="center"
            sx={{ pt:'4px', borderTop:`1px solid ${G.line}` }}
          >
            {/* Nëntë pilula u bënë gjashtë, dhe të gjashtat mbajnë numra të
                matur. Tri ikën: «Prod. paid» tregonte të njëjtin numër si
                «Productive», kurse «Prod. UnPaid» dhe «Admin» ishin literalë
                `"0:00"` te kodi — jo të dhëna që dilnin zero, por tekst i
                shkruar me dorë që dukej si matje. */}
            {/* Përqindjet (kërkesë e klientit): e punuara ndaj planit, produktivja
                dhe pauza ndaj të punuarës, mungesat e pushimet ndaj ditëve të
                punës së periudhës — p.sh. 10 pushime mjekësore = sa % e ditëve. */}
            <StatPill hint={t('oret.hintWorked')} label={`${t('oret.worked')}:`}    value={fmtH(stats.total)}     colors={CAT_COLORS.productive}
              plan={stats.planned ? fmtH(stats.planned) : null} pct={pctOf(stats.total, stats.planned)} />
            <StatPill hint={t('oret.hintProductive')} label={`${t('oret.productive')}:`} value={fmtH(stats.productive)} colors={CAT_COLORS.productive_paid}
              pct={pctOf(stats.productive, stats.total)} />
            <StatPill hint={t('oret.hintBreak')} label={`${t('oret.breakLabel')}:`}     value={fmtH(stats.paused)}    colors={CAT_COLORS.pause}
              plan={stats.plannedBreak ? fmtH(stats.plannedBreak) : null} pct={pctOf(stats.paused, stats.total)} />
            <StatPill hint={t('oret.hintOvertime')} label={`${t('oret.overtime')}:`}  value={fmtH(stats.overtime)}  colors={CAT_COLORS.overtime} />
            {/* Puna në ditë pushimi: brenda «Punuar», por e dukshme veç — pa të,
                orët e së shtunës nuk shiheshin askund te numrat. */}
            {stats.dayOff > 0 && (
              <StatPill hint={`Orë të punuara në ditë pushimi — ${stats.dayOffDays} ditë`}
                label="Ditë pushimi:" value={fmtH(stats.dayOff)} colors={CAT_COLORS.overtime} />
            )}
            <StatPill hint={t('oret.hintAbsent')} label={`${t('oret.absentCount')}:`}    value={stats.absent}          colors={CAT_COLORS.absent}
              pct={pctOf(stats.absent, stats.workDays)} />
            {[...stats.leaves.values()].sort((a, b) => b.days - a.days).map((lv) => (
              <StatPill key={`lv-${lv.typeId}`} hint={`${lv.label}: ${lv.days} ditë nga ${stats.workDays} ditë pune të planifikuara`}
                label={`${lv.label}:`} value={`${lv.days}d`}
                colors={{ bg: leaveBg(lv.typeId), fg: leaveTint(lv.typeId) }}
                pct={pctOf(lv.days, stats.workDays)} />
            ))}
            <StatPill hint={t('oret.hintLate')} label={`${t('oret.lateLabel')}:`}    value={`${stats.late}m`}      colors={CAT_COLORS.overtime} />
            {/* Numrat e një dite në ecuri janë të pjesshëm me të drejtë; pa këtë
                shenjë ato lexohen si totali përfundimtar i ditës. */}
            {stats.live && (
              <Typography sx={{ fontSize:10, fontWeight:600, color:G.inkSoft, whiteSpace:'nowrap' }}>
                {t('oret.soFar')}
              </Typography>
            )}
            <Box sx={{ ml:'auto', display:'flex', alignItems:'center', gap:.75, fontSize:10.5, color:G.inkSoft, fontWeight:500 }}>
              <Typography sx={{ fontSize:10.5, fontWeight:600, color:G.inkMid }}>
                {filteredWorkers.length} {t('oret.workers')}
              </Typography>
              <Typography sx={{ fontSize:10, color:G.inkFaint }}>{visStartLabel}–{visEndLabel}</Typography>
              {loading && <CircularProgress size={11} />}
            </Box>
          </Stack>
        )}

        {/* ── Veprimi i zgjedhjes ──────────────────────────────────────────────
            Caktimi kishte vetëm një derë: një buton i vogël brenda qelizës së
            kokës së rrjetës, 190px të gjera, ngjitur pas checkbox-it «zgjidh të
            gjithë». Aty ai lexohej si etiketë e checkbox-it, dhe pa zgjedhje
            rrinte i çaktivizuar pa thënë pse — një klikim mbi të nuk bënte
            asgjë dhe asgjë në ekran nuk e shpjegonte.

            Ky rresht del VETËM kur ka zgjedhje, ndaj s'është kurrë kontroll i
            vdekur: nëse duket, punon. Rri te shiriti, në rrjedhë normale — jo
            brenda një koke `position:sticky` mbi një tabelë me rrëshqitje. */}
        {gridSelectedWorkers.size > 0 && (
          <Stack direction="row" alignItems="center" gap={1}
            sx={{ pt:'6px', mt:'4px', borderTop:`1px solid ${G.line}` }}>
            <Typography sx={{ fontSize:11.5, fontWeight:700, color:G.inkStrong }}>
              {gridSelectedWorkers.size} {t('oret.workers')} {t('oret.selected')}
            </Typography>
            {/* Dritarja e tërhequr thuhet PARA se të hapet forma: përndryshe
                njeriu e zbulon vetëm brenda saj, kur s'ka më ç'të krahasojë. */}
            {dragWindow && (
              <Typography title={t('oret.dragWindow')}
                sx={{ fontSize:11, fontWeight:800, color:'#1a6b7d',
                  bgcolor:'#E3F0F4', px:.75, py:.15, borderRadius:'6px' }}>
                {dragWindow.from}–{dragWindow.to}
              </Typography>
            )}
            <Button
              size="small" variant="contained" disableElevation
              data-testid="assign-shift"
              disabled={!canEditSchedule || quickAssigning}
              title={!canEditSchedule
                ? t('oret.noPermission')
                : quickAssigning ? t('oret.assigning')
                : t('oret.openAssignForm')}
              onClick={() => {
                if (!quickScheduleId && defaultScheduleId) setQuickScheduleId(defaultScheduleId);
                setAllocOpen(true);
              }}
              sx={{ py:.25, minHeight:26, fontSize:11.5 }}
            >
              {quickAssigning ? t('oret.assigning') : t('oret.assignShift')}
            </Button>
            <Box component="button" onClick={() => { setGridSelectedWorkers(new Set()); setDragWindow(null); lastClickedIdxRef.current = null; }}
              sx={{ border:'none', bgcolor:'transparent', cursor:'pointer', fontSize:11, color:G.inkSoft,
                    textDecoration:'underline', p:0, '&:hover':{ color:G.inkStrong } }}>
              {t('oret.clearSelection')}
            </Box>
          </Stack>
        )}
      </Paper>

      {/* ── GRID ──────────────────────────────────────────────────────────────── */}
      <Box ref={gridRef}
        onMouseDown={onGridMouseDown}
        onMouseMove={onGridMouseMove}
        onMouseUp={onGridMouseUp}
        // Lëshimi jashtë rrjetës e mbaron zgjedhjen po ashtu; përndryshe ajo
        // mbetet e ngecur dhe klikimi tjetër vazhdon një tërheqje të vjetër.
        onMouseLeave={onGridMouseUp}
        sx={{ flex:1, overflowX:'auto', overflowY:'auto', bgcolor:'#fff', position:'relative' }}>
        {dragBox && (
          <Box sx={{ position:'absolute', pointerEvents:'none', zIndex:6,
            left:dragBox.left, top:dragBox.top, width:dragBox.width, height:dragBox.height,
            border:'2px solid #1a6b7d', bgcolor:'rgba(26,107,125,.16)', borderRadius:'2px' }} />
        )}
        {period !== 'day' ? (
          /* Kolonat janë ditë, jo minuta — dhe klikimi mbi një ditë kthen te
             pamja e minutave për të, që shkallët të mos jenë dy rrugë të ndara. */
          <DayMatrix
            workers={filteredWorkers}
            workerMap={overviewWorkerMap}
            columns={rangeColumns}
            period={period}
            selDate={selDate}
            onPickDay={(d) => { setSelDate(d); changePeriod('day'); }}
            onOpenWorker={openIndividualSchedule}
          />
        ) : (
        <Box sx={{ minWidth: gridW }}>
          <table style={{
            borderCollapse:'collapse', tableLayout:'fixed',
            width: gridW, fontSize:10,
          }}>
            <thead style={{ position:'sticky', top:0, zIndex:4 }}>
              <TimeHeader nowSlot={nowSlot} visibleRange={visibleRange} slotMin={slotMin} showTeamleader={showTeamleader} showProject={showProject} showDepartment={showDepartment} weeks={overviewWeeks} dayNum={selDayNum} />
              {/* Slot count summary row */}
              <tr style={{ background:G.zebra }}>
                <th style={{
                  position:'sticky', left:0, zIndex:5,
                  background:G.zebra, borderRight: (showTeamleader || showProject || showDepartment) ? `1px solid ${G.edge}` : `2px solid ${G.edge}`,
                  fontSize:9.5, color:G.inkMid, fontWeight:600,
                  padding:'0 8px', textAlign:'left',
                  borderBottom:`2px solid ${G.edge}`,
                }}>
              {filteredWorkers.length} {t('oret.workers')} &nbsp;·&nbsp; {gridSelectedWorkers.size} {t('oret.selected')}
              {/* I njëjti veprim si te shiriti, mbajtur këtu si shkurtore.
                  `title` thotë pse s'punon kur s'punon: një buton i zbehtë pa
                  arsye lexohet si defekt, jo si kusht i paplotësuar. */}
              <Button size="small" variant="outlined"
                data-testid="assign-shift"
                disabled={!canEditSchedule || !gridSelectedWorkers.size || quickAssigning}
                title={!canEditSchedule
                  ? t('oret.noPermission')
                  : !gridSelectedWorkers.size ? t('oret.pickWorkerFirst')
                  : quickAssigning ? t('oret.assigning')
                  : t('oret.openAssignForm')}
                onClick={() => {
                  if (!quickScheduleId && defaultScheduleId) setQuickScheduleId(defaultScheduleId);
                  setAllocOpen(true);
                }}
                sx={{ ml: .75, py: 0, minHeight: 20, fontSize: 10, px: .75 }}
              >{t('oret.assignShift')}</Button>
              {/* Ndarës: pa të, checkbox-i «zgjidh të gjithë» ngjitej pas
                  butonit dhe të dy lexoheshin si një kontroll i vetëm. */}
              {filteredWorkers.length > 0 && (
                <Box component="span" sx={{ display:'inline-block', width:'1px', height:11,
                  bgcolor:G.edge, mx:1, verticalAlign:'middle' }} />
              )}
              {filteredWorkers.length > 0 && (
                <input
                  type="checkbox"
                  title={t('oret.selectAll')}
                  checked={filteredWorkers.every(w => gridSelectedWorkers.has(w.Id ?? w.id))}
                  onChange={e => {
                    if (e.target.checked) setGridSelectedWorkers(new Set(filteredWorkers.map(w => w.Id ?? w.id)));
                    else setGridSelectedWorkers(new Set());
                  }}
                  onClick={e => e.stopPropagation()}
                  style={{ cursor:'pointer', accentColor:'#2e7d32', width:12, height:12, verticalAlign:'middle' }}
                />
              )}
                </th>
                {showTeamleader && <th style={{ position:'sticky', left:NAME_W, zIndex:5, background:G.zebra, borderRight: (showProject || showDepartment) ? `1px solid ${G.edge}` : `2px solid ${G.edge}`, borderBottom:`2px solid ${G.edge}` }} />}
                {showProject && <th style={{ position:'sticky', left:NAME_W+(showTeamleader?TL_COL_W:0), zIndex:5, background:G.zebra, borderRight: showDepartment ? `1px solid ${G.edge}` : `2px solid ${G.edge}`, borderBottom:`2px solid ${G.edge}` }} />}
                {showDepartment && <th style={{ position:'sticky', left:NAME_W+(showTeamleader?TL_COL_W:0)+(showProject?PROJ_COL_W:0), zIndex:5, background:G.zebra, borderRight:`2px solid ${G.edge}`, borderBottom:`2px solid ${G.edge}` }} />}
                {Array.from({ length: visEnd - visStart }, (_, i) => {
                  const s = visStart + i;
                  const cnt = slotCounts[s] || 0;
                  const min = SLOT_START + s * slotMin;
                  const isHour = min % 60 === 0;
                  return (
                    <th key={s} style={{
                      width:CELL_W, minWidth:CELL_W, maxWidth:CELL_W,
                      fontSize:7.5, fontWeight:cnt > 0 ? 700 : 400,
                      color: cnt > 0 ? '#1565c0' : '#ddd',
                      textAlign:'center', padding:0, height:18,
                      background: cnt > 0 ? '#eef4fc' : G.zebra,
                      borderLeft: isHour ? `2px solid ${G.lineHard}` : `1px solid ${G.lineSoft}`,
                      borderBottom:`2px solid ${G.edge}`,
                    }}>
                      {cnt > 0 ? cnt : ''}
                    </th>
                  );
                })}
                {/* empty cells for right cols + weeks + grand */}
                {RIGHT_COL_DEFS.map(c => <th key={c.key} style={{ background:G.zebra, borderLeft:'1px solid #e8ecf2', borderBottom:`2px solid ${G.edge}` }} />)}
                {overviewWeeks.map(wk => <th key={`wkc${wk.iso_week}`} style={{ background:G.zebra, borderLeft:`1px solid ${G.edgeStrong}`, borderBottom:`2px solid ${G.edge}` }} />)}
                {/* Sa kolona janë vërtet te totalet e mëdha: katër. E pesta shtonte një
                    kolonë fantazmë dhe e zhvendoste rreshtin e numrave. */}
                {['gt','gp','ga','gd'].map(k => <th key={k} style={{ background:G.zebra, borderLeft:`1px solid ${G.lineMid}`, borderBottom:`2px solid ${G.edge}` }} />)}
              </tr>
            </thead>

            <tbody>
              {filteredWorkers.length === 0 && !loading && (
                <tr>
                  <td colSpan={99} style={{ textAlign:'center', padding:40, color:G.inkFaint, fontSize:12 }}>
                    {(() => {
                      if (workersLoading) return t('oret.loadingWorkers');
                      if (workersError?.type === 'auth') {
                        return (
                          <>
                            <div style={{ color:'#c62828', fontWeight:700, marginBottom:6 }}>Autentifikimi ka skaduar ({workersError.detail})</div>
                            <div>Dil dhe identifikohu sërish për të rinovuar token-in.</div>
                          </>
                        );
                      }
                      if (workersError?.type === 'http' || workersError?.type === 'network') {
                        return (
                          <>
                            <div style={{ color:'#c62828', fontWeight:700, marginBottom:6 }}>
                              Gabim gjatë ngarkimit të punëtorëve ({workersError.detail})
                            </div>
                            <div>Kontrollo nëse serveri është aktiv dhe shiko Network tab në DevTools.</div>
                          </>
                        );
                      }
                      if (workersError?.type === 'all_inactive') {
                        return (
                          <>
                            <div style={{ color:'#ef6c00', fontWeight:700, marginBottom:6 }}>Të gjithë punëtorët janë të çaktivizuar (terminuar)</div>
                            <div>Hap <i>STAFI → Punëtorët</i> dhe reaktivizo punëtorin e dëshiruar, ose hiq ndërprerjen.</div>
                          </>
                        );
                      }
                      if (workersError?.type === 'empty_db') {
                        return (
                          <>
                            <div style={{ fontWeight:700, marginBottom:6 }}>Asnjë punëtor në sistem</div>
                            <div>Shko te <i>STAFI → Shto punëtor</i> për të krijuar punëtorin e parë.</div>
                          </>
                        );
                      }
                      if (search.trim()) return `Asnjë punëtor nuk përputhet me kërkimin "${search}"`;
                      return t('oret.noWorkers');
                    })()}
                  </td>
                </tr>
              )}

              {filteredWorkers.map(w => {
                const wid     = w.Id ?? w.id;
                const dayData = workerDayMap[wid] ?? null;
                const slots   = slotsMap[wid]    ?? new Array(slotCount).fill('empty');

                return (
                  <WorkerRow
                    key={wid}
                    worker={w}
                    dayData={dayData}
                    slots={slots}
                    visibleRange={visibleRange}
                    nowSlot={nowSlot}
                    slotMin={slotMin}
                    gridSelected={gridSelectedWorkers.has(wid)}
                    onGridSelect={(id, shiftKey) => {
                      // Tërheqja sapo e bëri zgjedhjen; klikimi që vjen pas saj
                      // do ta zhbënte atë menjëherë.
                      if (suppressRowClickRef.current) { suppressRowClickRef.current = false; return; }
                      toggleGridWorker(id, shiftKey,
                        filteredWorkers.findIndex(w => (w.Id ?? w.id) === id), filteredWorkers);
                    }}
                    showTeamleader={showTeamleader}
                    showProject={showProject}
                    workerProject={workerProjectMap[wid] || null}
                    showDepartment={showDepartment}
                    workerDept={w.DepartmentName || w.department_name || ''}
                    overviewWorker={overviewWorkerMap[wid] || null}
                    weeks={overviewWeeks}
                    selDate={selDate}
                    showMonitoring={showMonitoring}
                    onOpenSchedule={openIndividualSchedule}
                  />
                );
              })}
            </tbody>
          </table>
        </Box>
        )}
      </Box>

      {/* Këtu qëndronte një ftesë pluskuese: numri i të zgjedhurve, një buton
          që hapte formën dhe një ✕ për ta pastruar. Ajo u hoq.

          Arsyeja: forma tashmë hapet vetë sapo zgjedhja qetësohet, ndaj butoni
          «Cakto orarin» nuk kishte çfarë të hapte që s'ishte hapur — dhe pas
          mbylljes rrinte aty si mbetje e një veprimi të mbaruar. Numri i të
          zgjedhurve thuhet gjithsesi te koka e rrjetës («Selected: N») dhe
          rreshtat janë të gjelbër; një kuti e dytë që e përsëriste atë ishte
          zhurmë mbi një ekran ku kishte punë për të parë.

          Mbylljen e formës e trajton `onClose`: zgjedhja pastrohet bashkë me
          të, sepse ekziston vetëm për ta ushqyer atë. */}

      {/* Njoftimi i caktimit — i pavarur nga ftesa që u hoq. */}
      {quickSnack && (
        <Box sx={{
          position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)', zIndex:1400,
          bgcolor: quickSnack.startsWith('✓') ? QA.success : QA.warning,
          color:'#fff', borderRadius:'10px', px:2.25, py:1, whiteSpace:'nowrap',
          fontSize:12.5, fontWeight:700, boxShadow:'0 8px 26px rgba(0,0,0,.35)',
        }}>
          {quickSnack}
        </Box>
      )}

      {/* «Rregullimi i orarit» — forma e plotë e caktimit. Ajo që shkruan
          njeriu i shkon `handleQuickAssign` e gatshme, jo përmes gjendjes,
          sepse `setState` është asinkron dhe do të dërgonte vlerat e
          mëparshme. */}
      <ScheduleAllocationDialog
        open={allocOpen}
        /* Mbyllja e formës pastron edhe zgjedhjen: ajo ekziston vetëm për ta
           ushqyer atë. Pa këtë, rreshtat do të mbeteshin të gjelbër pas një
           «Anulo» dhe forma do të rihapej sapo të prekej dikush tjetër. */
        onClose={() => {
          setAllocOpen(false);
          setGridSelectedWorkers(new Set());
          setQuickScheduleId(''); setQuickEffectiveDate(''); setQuickEffectiveTo('');
          setQuickStartTime(''); setQuickEndTime(''); setQuickBreakSched('');
          setQuickShortBreaks([]); setQuickLongBreakType(''); setQuickLongBreakStart(''); setDragWindow(null); setQuickDepartment(''); setQuickProjectSegs([]);
          setQuickDescription('');
          lastClickedIdxRef.current = null;
        }}
        saving={quickAssigning}
        workerCount={gridSelectedWorkers.size}
        // Forma kontrollon vetë çfarë ka tashmë ky interval; për këtë i duhen
        // ID-të, jo vetëm numri. I memorizuara: një varg i ri në çdo render do
        // ta rinisë efektin e saj pafundësisht.
        workerIds={selectedWorkerIds}
        shiftStart={dragWindow?.from || quickStartTime}
        shiftEnd={dragWindow?.to || quickEndTime}
        departments={departments}
        projects={projects}
        mixedDepartment={gridSelectedWorkers.size > 1 && !profileDefaults.departmentId}
        mixedProject={gridSelectedWorkers.size > 1 && !profileDefaults.projectId}
        initial={{
          departmentId: quickDepartment || '',
          projects: quickProjectSegs.map((sg, i) => ({
            id: String(sg.id ?? i), projectId: sg.project_id ? String(sg.project_id) : '',
            from: sg.start || '', to: sg.end || '',
            workingTypeCode: sg.working_type_code || '',
          })),
          from:  quickEffectiveDate || selDate,
          to:    quickEffectiveTo || quickEffectiveDate || selDate,
          noEnd: false,
          // `{time, min}` → NGA–DERI. Pauza e gjatë e vetë orarit hyn si rreshti
          // i parë, i shënuar «Gjatë»: pa të, forma do të hapej sikur ai orar
          // s'kishte fare drekë, dhe ruajtja do ta fshinte.
          breaks: [
            ...(quickBreakSched !== '' && Number(quickBreakSched) > 0 && quickStartTime ? [(() => {
              // Ora e ruajtur fiton mbi mesin e turnit: mesi ishte hamendje për
              // mungesë të dhënash, jo zgjedhje.
              const mid = toMin(quickLongBreakStart)
                ?? ((toMin(quickStartTime) ?? 0) + Math.round(((toMin(quickEndTime) ?? 0) - (toMin(quickStartTime) ?? 0)) / 2));
              const hhmm = (m) => `${String(Math.floor(m / 60) % 24).padStart(2,'0')}:${String(m % 60).padStart(2,'0')}`;
              return { id: 'long', kind: 'long', workingTypeCode: quickLongBreakType || '',
                       from: hhmm(mid), to: hhmm(mid + Number(quickBreakSched)) };
            })()] : []),
            ...quickShortBreaks.map((b, i) => {
              const st = String(b.time || '');
              const m  = toMin(st);
              const mins = Number(b.min) || 0;
              const e = m == null ? '' :
                `${String(Math.floor((m + mins) / 60) % 24).padStart(2,'0')}:${String((m + mins) % 60).padStart(2,'0')}`;
              return { id: String(b.id ?? i), kind: 'short', workingTypeCode: b.working_type_code || '', from: st, to: e };
            }),
          ],
          description: quickDescription,
        }}
        onSave={async (form) => {
          const payload = allocToPayload(form);
          // Gjendja përditësohet që forma të rihapet ashtu si u la, edhe nëse
          // caktimi dështon për ndonjë punëtor.
          setQuickDepartment(form.departmentId || '');
          setQuickProjectSegs(form.projects.map((pr, i) => ({
            id: pr.id ?? i, project_id: pr.projectId, start: pr.from, end: pr.to,
            working_type_code: pr.workingTypeCode || '',
          })));
          setQuickEffectiveDate(form.from || '');
          setQuickEffectiveTo(form.noEnd ? '' : (form.to || ''));
          const longRow = form.breaks.find(b => b.kind === 'long' && b.from && b.to);
          setQuickBreakSched(longRow ? String(allocSpan(longRow.from, longRow.to)) : '');
          setQuickLongBreakType(longRow?.workingTypeCode || '');
          setQuickLongBreakStart(longRow?.from || '');
          setQuickLongBreakStart(longRow?.from || '');
          setQuickShortBreaks(form.breaks
            .filter(b => b.from && b.to && b.kind !== 'long')
            .map((b, i) => ({ id: b.id ?? i, time: b.from, min: String(allocSpan(b.from, b.to)),
                              working_type_code: b.workingTypeCode || '' })));
          setQuickDescription(form.description || '');
          setAllocOpen(false);
          await handleQuickAssign(payload);
        }}
      />


      {/* ── LEGEND ────────────────────────────────────────────────────────────── */}
      <Box sx={{
        px:2, py:.75,
        borderTop:`1px solid ${G.lineSoft}`,
        display:'flex', gap:2, flexWrap:'wrap', alignItems:'center',
        bgcolor:'#fff', flexShrink:0,
      }}>
        {/* Dy legjenda, dhe secila e thotë ku vlen.
            Brenda orarit të planifikuar ngjyrat i jep monitorimi (legjenda te
            shiriti lart); këto këtu janë ato jashtë tij. Pa këtë dallim, dy
            fjalorë ngjyrash mbi të njëjtën rrjetë duken si kontradiktë.

            «Admin» u hoq: ajo ngjyrë nuk vizatohet kurrë, sepse `buildSlots()`
            s'e prodhon dot pa të dhëna admin. */}
        {/* Llojet e pushimit që janë VËRTET në ekran atë ditë — jo katalogu i
            plotë. Një legjendë me dhjetë lloje ku dy janë të pranishme mëson të
            shpërfillet. */}
        {leaveLegend.length > 0 && (
          <>
            <Typography sx={{ fontSize:9.5, fontWeight:700, color:G.inkSoft, textTransform:'uppercase', letterSpacing:'.05em' }}>
              {t('oret.leaves')}
            </Typography>
            {leaveLegend.map(l => (
              <Box key={l.TypeId ?? l.Label} sx={{ display:'flex', alignItems:'center', gap:.5 }}>
                <Box sx={{ width:14, height:14, borderRadius:'4px',
                  bgcolor: leaveBg(l.TypeId), border:`1px solid ${leaveTint(l.TypeId)}` }} />
                <Typography sx={{ fontSize:10.5, color:G.inkStrong, fontWeight:500 }}>{l.Label}</Typography>
              </Box>
            ))}
            <Box sx={{ width:'1px', height:14, bgcolor:G.lineSoft, mx:.25 }} />
          </>
        )}
        <Typography sx={{ fontSize:9.5, fontWeight:700, color:G.inkSoft, textTransform:'uppercase', letterSpacing:'.05em' }}>
          {t('oret.outsideShift')}
        </Typography>
        {[
          { color: SLOT_COLORS.work,     label:t('oret.work') },
          { color: SLOT_COLORS.break,    label:t('oret.breakLabel') },
          { color: SLOT_COLORS.overtime, label:t('oret.overtimeShort') },
          { color: SLOT_COLORS.absent,   label:t('oret.absent') },
          { color: SLOT_COLORS.off,      label:t('oret.leave') },
        ].map(item => (
          <Box key={item.label} sx={{ display:'flex', alignItems:'center', gap:.5 }}>
            <Box sx={{ width:14, height:14, bgcolor:item.color, borderRadius:'4px', border:`1px solid ${G.edge}`, boxShadow:'0 1px 2px rgba(0,0,0,.05)' }} />
            <Typography sx={{ fontSize:10.5, color:G.lineHard, fontWeight:500 }}>{item.label}</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize:9.5, color:G.inkFaint, ml:'auto', fontWeight:500 }}>
          1 kolonë = {slotMin} min · {visEnd - visStart} kolona · {fmtH((visEnd - visStart) * slotMin)} të dukshme
        </Typography>
      </Box>

    </Box>
  );
}
